/* eslint-disable max-lines, max-lines-per-function */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import lottie from 'lottie-web';
import { TableUtility } from '@utility/dynamic-table';
import { Dialog as PrimeReactDialog } from 'primereact/dialog';
import { Button as PrimeReactButton } from 'primereact/button';
import './RevenueMis.scss';

import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

import {
  appendServiceNameToURL,
  appendServicePortToURL,
} from '../../../Api/resources/serviceURLs';
import { serviceName } from '../../../Api/resources/serviceConstants';
import addIcon from '../../../Assets/images/svg/dar_add.svg';
import alertIcon from '../../../Assets/images/svg/alert_icon.svg';
import calendarIcon from '../../../Assets/images/svg/calender_icon.svg';
import deleteIcon from '../../../Assets/images/svg/dar_delete.svg';
import editIcon from '../../../Assets/images/svg/dar_edit.svg';
import verticalDotIcon from '../../../Assets/images/svg/dar_more.svg';
import { useAuth } from '../../../Hooks/useAuth';
import { useSnackbar } from '../../../Components/Snackbar/snackBarProvider';
import normalizeTableUtilityResponse from '../../../Utils/tableUtilityResponse';
import {
  buildDetailedRevenueMisTableConfig,
  buildRevenueMisTableConfig,
} from './RevenueMisData';
import { MODULE_NAMES } from '../../../Constants/modules';

const getStoredAuthToken = () => localStorage.getItem('authToken') || '';

const REVENUE_MIS_FILTER_TEMPLATES = {
  revenueMisVerticalFilter: 'multiSelect',
  revenueMisTypeFilter: 'multiSelect',
  revenueMisStatusFilter: 'multiSelect',
  revenueMisManagerFilter: 'multiSelect',
  revenueMisProjectNameFilter: 'multiSelect',
  revenueMisClientNameFilter: 'multiSelect',
  revenueMisPoNumberFilter: 'multiSelect',
  revenueMisRevenueDateRangeFilter: 'dateRange',
  revenueMisRevenueTypeFilter: 'multiSelect',
};
const DEFAULT_REVENUE_MIS_PAGINATION = Object.freeze({
  page: 1,
  limit: 10,
  total: 0,
});
const REVENUE_MIS_SHOW_ALL_ROWS_LIMIT = 10000;

const REVENUE_MIS_NUMERIC_FIELD_PATTERN = /(?:^|_)totalRevenue(?:Inr|Usd)$|^budgetedFxRate$/i;
const REVENUE_MIS_NUMERIC_HEADER_PATTERN = /Total Revenue\((INR|USD)\)|Budgeted FX rate/i;

const buildProjectServiceApiUrl = (path) => {
  const serviceURL = appendServiceNameToURL(serviceName.PROJECT_MS);
  const servicePort = appendServicePortToURL(serviceName.PROJECT_MS);
  const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  if (process.env.NODE_ENV === 'production') {
    return `${baseUrl}${serviceURL}/api${path}`;
  }

  return `${baseUrl}:${servicePort}${serviceURL}/api${path}`;
};

const applyRevenueMisNumericAlignment = (column = {}) => {
  const columnWithOverflow = ['zohoCustomerName', 'milestoneZohoCustomerName'].includes(column?.field)
    ? { ...column, ellipsis: true }
    : column;
  const isNumericColumn = column?.numeric === true
    || column?.type === 'number'
    || REVENUE_MIS_NUMERIC_FIELD_PATTERN.test(column?.field || '')
    || REVENUE_MIS_NUMERIC_HEADER_PATTERN.test(`${column?.header || ''} ${column?.subHeader || ''}`);

  if (!isNumericColumn) return columnWithOverflow;

  return {
    ...columnWithOverflow,
    type: column.type || 'number',
    numeric: true,
    align: 'right',
    headerAlign: 'right',
    style: { ...(column.style || {}), textAlign: 'right' },
    headerStyle: { ...(column.headerStyle || {}), textAlign: 'right' },
  };
};

const REVENUE_RECOGNITION_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const REVENUE_MIS_UPLOAD_HEADERS = [
  ['JV No', 'JV. No'],
  ['Project Id'],
  ['Revenue Type'],
  ['Accrual/Reversal Date'],
  ['Accrued Entity'],
  ['Milestone ID'],
  ['Accrued/Reversed Amt (INR)', 'Accrued/ Reversed Amt (INR)'],
  ['Revenue Remarks (if Any)'],
];

const ALLOWED_REVENUE_TYPES = new Set([
  'accrual',
  'reversal',
  'credit note',
  'cost',
  'deferred revenue',
]);
const REVENUE_MIS_ROWS_PER_PAGE_OPTIONS = [5, 10, 20, 50];

const parseRevenueRecognitionDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value === 'number') {
    const numericDate = new Date(value);
    return Number.isNaN(numericDate.getTime()) ? null : numericDate;
  }
  if (typeof value !== 'string') return null;

  const trimmedValue = value.trim();
  if (!trimmedValue) return null;

  const directDate = new Date(trimmedValue);
  if (!Number.isNaN(directDate.getTime())) return directDate;

  const slashDateMatch = trimmedValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (!slashDateMatch) return null;

  const [, day, month, year] = slashDateMatch;
  const normalizedYear = year.length === 2 ? `20${year}` : year;
  const slashDate = new Date(Number(normalizedYear), Number(month) - 1, Number(day));
  return Number.isNaN(slashDate.getTime()) ? null : slashDate;
};

const formatRevenueRecognitionMonth = (value) => {
  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (/^[A-Za-z]+\s*'\d{2}(,\s*[A-Za-z]+\s*'\d{2})*$/.test(trimmedValue)) {
      return trimmedValue;
    }
  }

  const parsedDate = parseRevenueRecognitionDate(value);
  if (!parsedDate) return value ?? '';

  const monthName = REVENUE_RECOGNITION_MONTHS[parsedDate.getMonth()];
  const shortYear = String(parsedDate.getFullYear()).slice(-2);
  return `${monthName}'${shortYear}`;
};

const normalizeUploadHeader = (value) => String(value || '')
  .replace(/^\uFEFF/, '')
  .trim()
  .replace(/\s+/g, ' ')
  .replace(/\.\s*/g, '')
  .toLowerCase();

const isLeapYear = (year) => (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);

const getMaxDaysInMonth = (year, month) => {
  if (month < 1 || month > 12) return 0;
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  if ([4, 6, 9, 11].includes(month)) return 30;
  return 31;
};

const formatAndValidateYMD = (yearNum, monthNum, dayNum) => {
  if (
    Number.isNaN(yearNum)
    || Number.isNaN(monthNum)
    || Number.isNaN(dayNum)
    || yearNum < 1900
    || yearNum > 2100
    || monthNum < 1
    || monthNum > 12
    || dayNum < 1
  ) {
    return '';
  }

  const maxDays = getMaxDaysInMonth(yearNum, monthNum);
  if (dayNum > maxDays) {
    return '';
  }

  const yearStr = String(yearNum);
  const monthStr = String(monthNum).padStart(2, '0');
  const dayStr = String(dayNum).padStart(2, '0');
  return `${yearStr}-${monthStr}-${dayStr}`;
};

const parseRevenueUploadDate = (value) => {
  if (!hasMeaningfulValue(value)) return '';

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    return formatAndValidateYMD(
      value.getFullYear(),
      value.getMonth() + 1,
      value.getDate(),
    );
  }

  if (typeof value === 'number') {
    const numericDate = new Date(value);
    if (Number.isNaN(numericDate.getTime())) return '';
    return formatAndValidateYMD(
      numericDate.getFullYear(),
      numericDate.getMonth() + 1,
      numericDate.getDate(),
    );
  }

  const normalizedValue = String(value).trim();
  if (!normalizedValue) return '';

  const isoMatch = normalizedValue.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
  if (isoMatch) {
    const [, isoYear, isoMonth, isoDay] = isoMatch;
    return formatAndValidateYMD(Number(isoYear), Number(isoMonth), Number(isoDay));
  }

  const slashDateMatch = normalizedValue.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (slashDateMatch) {
    const [, part1, part2, yearPart] = slashDateMatch;
    const year = yearPart.length === 2 ? Number(`20${yearPart}`) : Number(yearPart);
    const num1 = Number(part1);
    const num2 = Number(part2);

    if (num1 > 12) {
      return formatAndValidateYMD(year, num2, num1);
    }
    if (num2 > 12) {
      return formatAndValidateYMD(year, num1, num2);
    }

    const tryDdMm = formatAndValidateYMD(year, num2, num1);
    if (tryDdMm) return tryDdMm;

    const tryMmDd = formatAndValidateYMD(year, num1, num2);
    if (tryMmDd) return tryMmDd;

    return '';
  }

  const directDate = new Date(normalizedValue);
  if (!Number.isNaN(directDate.getTime())) {
    const directYear = directDate.getFullYear();
    const directMonth = directDate.getMonth() + 1;
    const directDay = directDate.getDate();

    // Verify JS didn't roll over (e.g. "4/31/2029" -> May 1)
    const normalizedLower = normalizedValue.toLowerCase();
    if (normalizedLower.includes('31') && directDay !== 31) return '';
    if (normalizedLower.includes('30') && directDay !== 30 && directDay !== 31) return '';
    if (normalizedLower.includes('29') && directMonth === 2 && directDay !== 29) return '';

    return formatAndValidateYMD(directYear, directMonth, directDay);
  }

  return '';
};

const parseRevenueUploadAmount = (value) => {
  if (!hasMeaningfulValue(value)) return null;

  const normalizedValue = typeof value === 'number'
    ? value
    : Number(
      String(value)
        .replace(/,/g, '')
        .trim(),
    );

  return Number.isFinite(normalizedValue) ? normalizedValue : null;
};

const getExcelColumnLetter = (index) => {
  let columnIndex = index + 1;
  let columnLetter = '';

  while (columnIndex > 0) {
    const remainder = (columnIndex - 1) % 26;
    columnLetter = String.fromCharCode(65 + remainder) + columnLetter;
    columnIndex = Math.floor((columnIndex - 1) / 26);
  }

  return columnLetter;
};

const buildRevenueMisUploadErrorMessage = (issues = [], limit = 10) => {
  if (!Array.isArray(issues) || issues.length === 0) {
    return '';
  }

  const visibleIssues = issues.slice(0, limit).map((issue) => `- ${issue}`);
  const remainingCount = issues.length - visibleIssues.length;

  return [
    'Please correct the following upload issues:',
    ...visibleIssues,
    ...(remainingCount > 0 ? [`- ...and ${remainingCount} more issue(s).`] : []),
  ].join('\n');
};

const validateRevenueMisUploadHeaders = (headers = []) => {
  const normalizedHeaders = Array.isArray(headers)
    ? headers.map(normalizeUploadHeader)
    : [];

  const expectedHeaderDefinitions = REVENUE_MIS_UPLOAD_HEADERS.map((headerVariants, index) => ({
    label: headerVariants[0],
    aliases: headerVariants.map(normalizeUploadHeader),
    columnLetter: getExcelColumnLetter(index),
  }));

  const issues = [];

  expectedHeaderDefinitions.forEach((definition, index) => {
    const actualHeader = normalizedHeaders[index] || '';
    if (!definition.aliases.includes(actualHeader)) {
      const receivedHeader = String(headers[index] || '').trim() || '(blank)';
      issues.push(
        `Column ${definition.columnLetter} should be "${definition.label}" but found "${receivedHeader}".`,
      );
    }
  });

  if (normalizedHeaders.length > expectedHeaderDefinitions.length) {
    headers.slice(expectedHeaderDefinitions.length).forEach((header, extraIndex) => {
      const extraColumnLetter = getExcelColumnLetter(
        expectedHeaderDefinitions.length + extraIndex,
      );
      const extraColumnLabel = String(header || '').trim() || '(blank)';
      issues.push(
        `Unexpected extra column ${extraColumnLetter}: "${extraColumnLabel}".`,
      );
    });
  }

  if (normalizedHeaders.length < expectedHeaderDefinitions.length) {
    expectedHeaderDefinitions.slice(normalizedHeaders.length).forEach((definition) => {
      issues.push(`Column ${definition.columnLetter} (${definition.label}) is missing.`);
    });
  }

  return issues;
};

const validateRevenueMisUploadRow = (row = [], rowNumber = 0) => {
  const [
    jvNo,
    projectId,
    revenueType,
    accrualDate,
    accruedEntity,
    milestoneId,
    amount,
    remarks,
  ] = row;

  const normalizedJvNo = String(jvNo || '').trim();
  const normalizedProjectId = String(projectId || '').trim();
  const normalizedRevenueType = String(revenueType || '').trim();
  const normalizedAccruedEntity = String(accruedEntity || '').trim();
  const normalizedMilestoneId = String(milestoneId || '').trim();
  const normalizedRemarks = hasMeaningfulValue(remarks) ? String(remarks).trim() : '';
  const normalizedAccrualDate = parseRevenueUploadDate(accrualDate);
  const normalizedAmount = parseRevenueUploadAmount(amount);

  const isEmptyRow = [
    normalizedJvNo,
    normalizedProjectId,
    normalizedRevenueType,
    normalizedAccrualDate,
    normalizedAccruedEntity,
    normalizedMilestoneId,
    normalizedRemarks,
  ].every((cell) => !cell) && normalizedAmount === null;

  if (isEmptyRow) {
    return { isEmptyRow: true, issues: [], value: null };
  }

  const issues = [];

  if (!normalizedJvNo) {
    issues.push(`Row ${rowNumber}, Column A (JV No): value is required.`);
  }

  if (!normalizedProjectId) {
    issues.push(`Row ${rowNumber}, Column B (Project Id): value is required.`);
  }

  if (!normalizedRevenueType) {
    issues.push(`Row ${rowNumber}, Column C (Revenue Type): value is required.`);
  } else if (!ALLOWED_REVENUE_TYPES.has(normalizedRevenueType.toLowerCase())) {
    issues.push(
      [
        `Row ${rowNumber}, Column C (Revenue Type): must be Accrual, Reversal,`,
        `Credit Note, Cost, or Deferred Revenue, found "${normalizedRevenueType}".`,
      ].join(' '),
    );
  }

  if (!hasMeaningfulValue(accrualDate)) {
    issues.push(`Row ${rowNumber}, Column D (Accrual/Reversal Date): value is required.`);
  } else if (!normalizedAccrualDate) {
    issues.push(`Row ${rowNumber}, Column D (Accrual/Reversal Date): wrong or invalid date.`);
  }

  if (!normalizedAccruedEntity) {
    issues.push(`Row ${rowNumber}, Column E (Accrued Entity): value is required.`);
  }

  if (!normalizedMilestoneId) {
    issues.push(`Row ${rowNumber}, Column F (Milestone ID): value is required.`);
  }

  if (normalizedAmount === null) {
    issues.push(`Row ${rowNumber}, Column G (Accrued/Reversed Amt (INR)): must be a valid number.`);
  }

  return {
    isEmptyRow: false,
    issues,
    value: {
      rowNumber,
      uploadRowNumber: rowNumber - 2,
      jvNo: normalizedJvNo,
      projectId: normalizedProjectId,
      revenueType: normalizedRevenueType,
      accrualDate: normalizedAccrualDate,
      accruedEntity: normalizedAccruedEntity,
      milestoneId: normalizedMilestoneId,
      amount: normalizedAmount,
      remarks: normalizedRemarks,
    },
  };
};

const parseRevenueUploadFile = async (file) => {
  const fileName = String(file?.name || '').toLowerCase();
  const fileExtension = fileName.split('.').pop();
  let rows = [];

  if (fileExtension === 'csv') {
    const Papa = (await import('papaparse')).default;
    const csvText = await file.text();
    const parsedCsv = Papa.parse(csvText, {
      skipEmptyLines: 'greedy',
    });

    if (parsedCsv.errors?.length > 0) {
      throw new Error(parsedCsv.errors[0]?.message || 'Unable to read CSV file.');
    }

    rows = Array.isArray(parsedCsv.data) ? parsedCsv.data : [];
  } else {
    const readXlsxFile = (await import('read-excel-file')).default;
    rows = await readXlsxFile(file);
  }

  if (!Array.isArray(rows) || rows.length <= 1) {
    throw new Error('Uploaded file is empty.');
  }

  const [headers, ...dataRows] = rows;
  const headerIssues = validateRevenueMisUploadHeaders(headers);

  if (headerIssues.length > 0) {
    throw new Error(buildRevenueMisUploadErrorMessage(headerIssues, headerIssues.length));
  }

  const validationIssues = [];
  const uploadRows = dataRows
    .map((row = [], index) => {
      const rowNumber = index + 2;
      const validationResult = validateRevenueMisUploadRow(row, rowNumber);
      validationIssues.push(...validationResult.issues);

      if (validationResult.isEmptyRow) {
        return null;
      }

      return validationResult.value;
    })
    .filter(Boolean);

  if (validationIssues.length > 0) {
    throw new Error(buildRevenueMisUploadErrorMessage(validationIssues));
  }

  if (uploadRows.length === 0) {
    throw new Error('Uploaded file is empty.');
  }

  return uploadRows;
};

const hasMeaningfulValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  return true;
};

const buildTableUtilityLocalExport = (rows = [], tableConfig = [], excludedFields = []) => {
  const excludedFieldSet = new Set(excludedFields);
  const exportColumns = (Array.isArray(tableConfig) ? tableConfig : [])
    .filter((column) => (
      column?.field
      && !column?.isGlobalFilter
      && !excludedFieldSet.has(column.field)
    ))
    .map((column) => ({
      field: column.field,
      header: column.header || column.label || column.field,
    }));

  return {
    tableConfiguration: exportColumns.map((column) => ({
      field: column.field,
      header: column.header,
      numeric: ['totalRevenueInr', 'totalRevenueUsd', 'budgetedFxRate'].includes(column.field),
    })),
    tableData: (Array.isArray(rows) ? rows : []).map((row) => exportColumns.reduce((acc, column) => {
      const rawValue = row?.[column.field];
      acc[column.field] = hasMeaningfulValue(rawValue) ? rawValue : '';
      return acc;
    }, {})),
  };
};

const getNestedValue = (source, path) => {
  if (!source || typeof source !== 'object' || !path) return undefined;
  return path.split('.').reduce((currentValue, key) => {
    if (currentValue === null || currentValue === undefined) return undefined;
    return currentValue[key];
  }, source);
};

const readFirstPresent = (source, ...paths) => {
  for (const path of paths) {
    const candidateValue = getNestedValue(source, path);
    if (hasMeaningfulValue(candidateValue)) return candidateValue;
  }
  return '';
};

const normalizeStatusKey = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]/g, '');

const REVENUE_MIS_FIELD_ALIASES = {
  accruedInvoicedDate: [
    'accruedInvoicedDate',
    'invoiceDate',
    'invoicedDate',
    'accruedDate',
    'revenueDate',
    'milestoneDate',
    'date',
  ],
  projectId: [
    'projectId',
    'project_id',
    'project.projectId',
    'project.project_id',
    'basicDetails.projectId',
  ],
  projectName: [
    'projectName',
    'project_name',
    'project.projectName',
    'project.project_name',
    'basicDetails.projectName',
  ],
  zohoCustomerName: [
    'zohoCustomerName',
    'customerName',
    'customer_name',
    'zohoClientName',
    'clientName',
    'client_name',
  ],
  milestoneZohoCustomerName: [
    'milestoneZohoCustomerName',
    'milestoneCustomerName',
    'zohoCustomerName',
    'customerName',
    'customer_name',
    'zohoClientName',
    'clientName',
  ],
  revenueDetailId: ['revenueDetailId', 'revenue_detail_id', 'id'],
  revenueSourceId: ['revenueSourceId', 'revenue_source_id', 'sourceRecordId', 'source_record_id'],
  revenueRowSource: ['revenueRowSource', 'revenue_row_source', 'source'],
  projectStatus: ['projectStatus', 'project_status', 'status', 'project.status'],
  projectType: ['projectType', 'project_type', 'type', 'basicDetails.type'],
  actualProjectStartDate: [
    'actualProjectStartDate',
    'actual_project_start_date',
    'actualStartDate',
    'actual_start_date',
    'projectStartDate',
  ],
  projectVertical: ['projectVertical', 'project_vertical', 'vertical', 'project.vertical'],
  seniorManager: [
    'seniorManager',
    'senior_manager',
    'srManager',
    'sr_manager',
    'project.seniorManager',
  ],
  zohoBillingEntity: [
    'zohoBillingEntity',
    'zoho_billing_entity',
    'accruedEntity',
    'billingEntity',
    'entity',
  ],
  country: ['country', 'projectCountry', 'project_country'],
  region: ['region', 'projectRegion', 'project_region'],
  milestoneId: ['milestoneId', 'milestone_id', 'numericId', 'id'],
  milestoneCategory: [
    'milestoneCategory',
    'milestone_category',
    'milestoneName',
    'milestone_name',
    'category',
  ],
  milestoneStatus: ['milestoneStatus', 'milestone_status', 'milestoneStatusName'],
  invoiceStatus: [
    'invoiceStatus',
    'invoice_status',
    'invoiceStatusName',
    'invoice_status_name',
  ],
  revenueType: ['revenueType', 'revenue_type', 'recognitionType'],
  totalRevenueInr: [
    'totalRevenueInr',
    'totalRevenueINR',
    'total_revenue_inr',
    'revenueInr',
    'revenue_inr',
    'amountInr',
    'amount_inr',
    'amount',
  ],
  totalRevenueUsd: [
    'totalRevenueUsd',
    'totalRevenueUSD',
    'total_revenue_usd',
    'revenueUsd',
    'revenue_usd',
    'amountUsd',
    'amount_usd',
  ],
  budgetedFxRate: [
    'budgetedFxRate',
    'budgeted_fx_rate',
    'fxRate',
    'fx_rate',
    'conversionRate',
  ],
  zohoInvoiceOrJvNumber: [
    'zohoInvoiceOrJvNumber',
    'zoho_invoice_or_jv_number',
    'zohoInvoiceNo',
    'zoho_invoice_no',
    'invoiceNumber',
    'invoice_number',
    'jvNumber',
    'jv_number',
  ],
  invoicedAccruedZohoClientName: [
    'invoicedAccruedZohoClientName',
    'invoiced_accrued_zoho_client_name',
    'invoicedZohoClientName',
    'accruedZohoClientName',
    'zohoClientName',
    'clientName',
    'customerName',
  ],
  remarks: ['remarks', 'remark', 'comments', 'comment', 'note'],
};

const getRevenueMisNormalizedValue = (row, aliasKey) => (
  readFirstPresent(row, ...(REVENUE_MIS_FIELD_ALIASES[aliasKey] || []))
);

const REVENUE_MIS_STATUS_STYLES = {
  inprogress: { boxColor: '#eaf8ff', textColor: '#00a3ff' },
  inprogressproject: { boxColor: '#eaf8ff', textColor: '#00a3ff' },
  onhold: { boxColor: '#fff4d9', textColor: '#e8a700' },
  closed: { boxColor: '#f0ecff', textColor: '#7a57f7' },
  dropped: { boxColor: '#ffecef', textColor: '#e30425' },
  completed: { boxColor: '#eaffef', textColor: '#08b758' },
  pendingpo: { boxColor: '#fff4d9', textColor: '#e8a700' },
  pendingtoinvoice: { boxColor: '#fff4d9', textColor: '#e8a700' },
  cancelled: { boxColor: '#ffecef', textColor: '#e30425' },
  canceled: { boxColor: '#ffecef', textColor: '#e30425' },
};

const isRevenueMisCancelledStatusKey = (statusKey) => ['cancelled', 'canceled'].includes(statusKey);

const isCancelledRevenueMisRow = (row = {}) => {
  const status = readFirstPresent(row, 'projectStatus', 'project_status', 'status');
  return isRevenueMisCancelledStatusKey(normalizeStatusKey(status));
};

const buildRevenueMisStatusTemplateEntry = (field, value) => {
  const normalizedStatus = normalizeStatusKey(value);
  const style = field === 'projectStatus' && normalizedStatus === 'inprogress'
    ? REVENUE_MIS_STATUS_STYLES.inprogressproject
    : REVENUE_MIS_STATUS_STYLES[normalizedStatus];
  if (!style) return null;

  return {
    field,
    boxColor: style.boxColor,
    textColor: style.textColor,
    fontWeight: '500',
  };
};

const applyRevenueMisStatusStylesToRow = (row = {}) => {
  if (!row || typeof row !== 'object') return row;

  const existingTemplate = Array.isArray(row.rowConfiguration?.template)
    ? row.rowConfiguration.template
    : [];
  const statusTemplateEntries = [
    buildRevenueMisStatusTemplateEntry('projectStatus', row.projectStatus),
    buildRevenueMisStatusTemplateEntry('project_status', row.project_status),
    buildRevenueMisStatusTemplateEntry('status', row.status),
  ].filter(Boolean);

  if (statusTemplateEntries.length === 0) return row;

  const statusFields = new Set(statusTemplateEntries.map((entry) => entry.field));

  return {
    ...row,
    rowConfiguration: {
      ...(row.rowConfiguration || {}),
      template: [
        ...existingTemplate.filter((entry) => !statusFields.has(entry.field)),
        ...statusTemplateEntries,
      ],
    },
  };
};

const normalizeRevenueMisRow = (row = {}) => {
  const revenueRecognitionSource = readFirstPresent(row, ...[
    'revenueRecognitionMonth',
    'revenueRecognitionDate',
    'invoiceDate',
    'milestoneDate',
    'accruedInvoicedDate',
    'date',
  ]);

  const formatInr = (value) => {
    if (value === null || value === undefined) return value;
    if (typeof value === 'string' && value.trim() === '') return value;
    if (typeof value === 'string' && value.toLowerCase() === 'n/a') return value;

    const numeric = typeof value === 'number'
      ? value
      : Number(String(value).replace(/,/g, ''));
    if (!Number.isFinite(numeric)) return value;

    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numeric);
  };

  return applyRevenueMisStatusStylesToRow({
    ...row,
    rowSelector: '',
    revenueDetailId: getRevenueMisNormalizedValue(row, 'revenueDetailId'),
    revenueSourceId: getRevenueMisNormalizedValue(row, 'revenueSourceId'),
    revenueRowSource: getRevenueMisNormalizedValue(row, 'revenueRowSource'),
    accruedInvoicedDate: getRevenueMisNormalizedValue(row, 'accruedInvoicedDate'),
    projectId: getRevenueMisNormalizedValue(row, 'projectId'),
    projectName: getRevenueMisNormalizedValue(row, 'projectName'),
    zohoCustomerName: getRevenueMisNormalizedValue(row, 'zohoCustomerName'),
    milestoneZohoCustomerName: getRevenueMisNormalizedValue(row, 'milestoneZohoCustomerName'),
    projectStatus: getRevenueMisNormalizedValue(row, 'projectStatus'),
    projectType: getRevenueMisNormalizedValue(row, 'projectType'),
    actualProjectStartDate: getRevenueMisNormalizedValue(row, 'actualProjectStartDate'),
    projectVertical: getRevenueMisNormalizedValue(row, 'projectVertical'),
    seniorManager: getRevenueMisNormalizedValue(row, 'seniorManager'),
    zohoBillingEntity: getRevenueMisNormalizedValue(row, 'zohoBillingEntity'),
    country: getRevenueMisNormalizedValue(row, 'country'),
    region: getRevenueMisNormalizedValue(row, 'region'),
    milestoneId: getRevenueMisNormalizedValue(row, 'milestoneId'),
    milestoneCategory: getRevenueMisNormalizedValue(row, 'milestoneCategory'),
    milestoneStatus: getRevenueMisNormalizedValue(row, 'milestoneStatus'),
    invoiceStatus: getRevenueMisNormalizedValue(row, 'invoiceStatus'),
    revenueType: getRevenueMisNormalizedValue(row, 'revenueType'),
    totalRevenueInr: formatInr(getRevenueMisNormalizedValue(row, 'totalRevenueInr')),
    totalRevenueUsd: getRevenueMisNormalizedValue(row, 'totalRevenueUsd'),
    budgetedFxRate: getRevenueMisNormalizedValue(row, 'budgetedFxRate'),
    zohoInvoiceOrJvNumber: getRevenueMisNormalizedValue(row, 'zohoInvoiceOrJvNumber'),
    invoicedAccruedZohoClientName: getRevenueMisNormalizedValue(
      row,
      'invoicedAccruedZohoClientName',
    ),
    revenueRecognitionMonth: formatRevenueRecognitionMonth(revenueRecognitionSource),
    remarks: getRevenueMisNormalizedValue(row, 'remarks'),
    rowActions: '',
  });
};

const buildRevenueMisTotalRow = (summary = {}) => {
  if (!summary || typeof summary !== 'object' || Object.keys(summary).length === 0) {
    return null;
  }

  const normalizedSummary = normalizeRevenueMisRow({
    ...summary,
    projectName: readFirstPresent(summary, 'projectName', 'project_name') || 'Total',
    revenueDetailId: '',
    milestoneId: '',
    rowSelector: '',
    rowActions: '',
  });

  return {
    ...normalizedSummary,
    revenueDetailId: '',
    milestoneId: '',
    id: summary.id || normalizedSummary.id || 'summary-row',
    __rowId: summary.__rowId || normalizedSummary.__rowId || 'summary-row',
    isTotalRow: true,
    rowConfiguration: {
      ...(normalizedSummary.rowConfiguration || {}),
      ...(summary.rowConfiguration || {}),
      isFrozen: true,
      backgroundColor: summary.rowConfiguration?.backgroundColor || '#eceff5',
    },
  };
};

const isRevenueMisSummaryRow = (row = {}) => {
  const projectName = String(
    readFirstPresent(row, 'projectName', 'project_name', 'project') || '',
  )
    .trim()
    .toLowerCase();
  const revenueType = String(
    readFirstPresent(row, 'revenueType', 'revenue_type') || '',
  )
    .trim()
    .toLowerCase();
  const rowId = String(row?.__rowId || row?.id || '')
    .trim()
    .toLowerCase();

  return Boolean(
    row?.isTotalRow
    || row?.rowConfiguration?.isFrozen
    || rowId === 'summary-row'
    || rowId === 'revenue-mis-total-row'
    || projectName === 'total'
    || revenueType === 'total',
  );
};

const getRevenueMisRowKey = (row = {}) => ({
  jvNo: readFirstPresent(row, 'zohoInvoiceOrJvNumber'),
  milestoneId: readFirstPresent(row, 'milestoneId'),
});

const getRevenueMisSourceRecord = (row = {}) => ({
  source: readFirstPresent(row, 'revenueRowSource') || 'revenue_details',
  id: readFirstPresent(row, 'revenueSourceId', 'revenueDetailId'),
});

const isUploadedRevenueMisRow = (row = {}) => (
  !isRevenueMisSummaryRow(row)
  && String(readFirstPresent(row, 'revenueRowSource')).toLowerCase() === 'revenue_details'
  && Boolean(readFirstPresent(row, 'revenueDetailId', 'revenueSourceId'))
);

const hasFilterValue = (item) => {
  if (!item || item?.type !== 'filter') return false;
  if (Array.isArray(item?.selectedValues) && item.selectedValues.length > 0) return true;
  if (Array.isArray(item?.range)) return item.range.some(Boolean);
  return Boolean(item?.selectedValue ?? item?.inputValue ?? item?.value);
};

const getSelectedProjectFromConfiguration = (tableConfiguration = []) => {
  const appliedFilter = tableConfiguration
    .flatMap((column) => column?.menuItems || [])
    .flatMap((menuItem) => menuItem?.items || [])
    .find(hasFilterValue);
  return appliedFilter?.field || '';
};

const getAppliedFiltersFromConfiguration = (tableConfiguration = []) => (
  tableConfiguration
    .flatMap((column) => column?.menuItems || [])
    .flatMap((menuItem) => menuItem?.items || [])
    .filter(hasFilterValue)
);

const getFilterItemValue = (item = {}) => {
  if (Array.isArray(item?.selectedValues) && item.selectedValues.length > 0) {
    return item.selectedValues.join(',');
  }
  if (Array.isArray(item?.range) && item.range.some(Boolean)) {
    return item.range.filter(Boolean).join(',');
  }
  return item?.selectedValue ?? item?.inputValue ?? item?.value ?? '';
};

const getFilterFingerprint = (filters = []) => (
  filters
    .filter(hasFilterValue)
    .map((item) => `${item.field}:${getFilterItemValue(item)}`)
    .sort()
    .join('|')
);

const mergeRevenueMisTableConfiguration = (
  apiTableConfiguration = [],
  fallbackTableConfiguration = [],
) => {
  const fallbackByField = new Map(
    fallbackTableConfiguration
      .filter((column) => column?.field)
      .map((column) => [column.field, column]),
  );
  const mergedFields = new Set();

  const mergedColumns = apiTableConfiguration.map((column) => {
    const fallbackColumn = fallbackByField.get(column?.field);
    mergedFields.add(column?.field);

    if (!fallbackColumn) return column;

    return {
      ...fallbackColumn,
      ...column,
      menuItems: Array.isArray(column?.menuItems) && column.menuItems.length > 0
        ? column.menuItems
        : fallbackColumn.menuItems || [],
    };
  });

  fallbackTableConfiguration.forEach((column) => {
    if (!mergedFields.has(column?.field)) {
      mergedColumns.push(column);
    }
  });

  return mergedColumns.sort((firstColumn, secondColumn) => (
    Number(firstColumn?.order || 0) - Number(secondColumn?.order || 0)
  ));
};

// ─── DOM helpers ────────────────────────────────────────────────────────────

const applyRevenueMisTableViewport = (rootNode) => {
  rootNode?.querySelectorAll('.table-container').forEach((el) => {
    el.classList.add('revenue-mis-table-viewport');
  });
};

const applyBodyCellHoverTitles = (rootNode) => {
  rootNode
    ?.querySelectorAll('.revenue-mis-table-utility .p-datatable-tbody > tr > td')
    .forEach((td) => {
      const content = td.querySelector('.cell-wrapper .ellipses')
        || td.querySelector('.cell-wrapper > span')
        || td.querySelector('span')
        || td.querySelector('div')
        || td;
      const fullText = (content.textContent || '').trim();
      if (fullText && content.scrollWidth > content.clientWidth) {
        td.setAttribute('title', fullText);
        content.setAttribute('title', fullText);
      } else {
        td.removeAttribute('title');
        if (content !== td) content.removeAttribute('title');
      }
    });
};

// ─── Detailed table enhancements ────────────────────────────────────────────

const applyToolbarButtonClasses = (rootNode) => {
  rootNode?.querySelectorAll([
    '.button-group button',
    '.project-overview-header button',
    '.revenue-mis-toolbar-header button',
  ].join(', '))
    .forEach((btn) => {
      btn.classList.toggle('revenue-mis-reset-btn', Boolean(btn.querySelector('.pi-refresh')));

      const tooltipAttr = btn.getAttribute('tooltip') || '';
      const ariaLabelAttr = btn.getAttribute('aria-label') || '';
      const prTooltipAttr = btn.getAttribute('data-pr-tooltip') || '';
      const hasDownloadSvg = btn.querySelector('svg path') || btn.querySelector('svg');
      const isDownload = tooltipAttr.toLowerCase().includes('download')
      || prTooltipAttr.toLowerCase().includes('download')
      || ariaLabelAttr.toLowerCase().includes('download')
      || (hasDownloadSvg
        && !btn.querySelector('.pi-refresh')
        && !btn.querySelector('.table-utility-filter-btn')
        && !btn.classList.contains('table-utility-filter-btn'));

      if (isDownload) {
        btn.classList.add('download-excel-button');
      }
    });
};

const reorderToolbarButtons = (rootNode) => {
  const buttonGroupElement = rootNode?.querySelector('.button-group');
  const filterBtn = buttonGroupElement?.querySelector('.table-utility-filter-btn');
  const downloadBtn = buttonGroupElement?.querySelector('.download-excel-button');
  if (buttonGroupElement && filterBtn && downloadBtn) {
    buttonGroupElement.insertBefore(downloadBtn, filterBtn);
  }
};

const getToolbarButtonKey = (buttonElement) => {
  if (!buttonElement) return '';
  return (
    buttonElement.getAttribute('aria-label')
    || buttonElement.getAttribute('title')
    || buttonElement.getAttribute('tooltip')
    || buttonElement.getAttribute('data-pr-tooltip')
    || buttonElement.textContent
    || ''
  ).trim().toLowerCase();
};

const isRevenueMisDownloadButton = (buttonElement) => {
  if (!buttonElement) return false;

  const buttonKey = getToolbarButtonKey(buttonElement);
  const hasDownloadSvg = Boolean(buttonElement.querySelector('svg path') || buttonElement.querySelector('svg'));

  return buttonElement.classList.contains('download-excel-button')
    || buttonKey.includes('download')
    || (
      hasDownloadSvg
      && !buttonElement.querySelector('.pi-refresh')
      && !buttonElement.classList.contains('table-utility-filter-btn')
      && buttonElement.getAttribute('aria-label') !== 'Revenue MIS actions'
    );
};

const moveToolbarButtonToHost = (buttonElement, hostElement, className) => {
  if (!buttonElement || !hostElement) return;
  const buttonKey = getToolbarButtonKey(buttonElement);
  if (buttonKey) {
    hostElement.querySelectorAll('button').forEach((existingButton) => {
      if (existingButton !== buttonElement && getToolbarButtonKey(existingButton) === buttonKey) {
        existingButton.remove();
      }
    });
  }
  if (className) buttonElement.classList.add(className);
  if (buttonElement.parentElement !== hostElement) {
    hostElement.appendChild(buttonElement);
  }
};

const syncConsolidatedToolbarButtons = (rootNode, pageHeaderHost, sectionHeaderHost) => {
  if (!rootNode) return;

  const buttonGroupElement = rootNode.querySelector('.button-group');
  const sourceButtons = Array.from(
    (buttonGroupElement || rootNode).querySelectorAll('button'),
  );
  if (!buttonGroupElement && !pageHeaderHost && !sectionHeaderHost) return;

  const filterButton = buttonGroupElement?.querySelector('.table-utility-filter-btn')
    || sourceButtons.find((buttonElement) => buttonElement.classList.contains('table-utility-filter-btn'))
    || pageHeaderHost?.querySelector('.table-utility-filter-btn');
  const downloadButton = buttonGroupElement?.querySelector('.download-excel-button')
    || sourceButtons.find(isRevenueMisDownloadButton)
    || sectionHeaderHost?.querySelector('.download-excel-button')
    || Array.from(sectionHeaderHost?.querySelectorAll('button') || []).find(isRevenueMisDownloadButton);
  const moreButton = sourceButtons
    .find((buttonElement) => buttonElement.getAttribute('aria-label') === 'Revenue MIS actions');

  moveToolbarButtonToHost(moreButton, pageHeaderHost, 'revenue-mis-title-action-btn');
  moveToolbarButtonToHost(filterButton, pageHeaderHost, 'revenue-mis-title-action-btn');
  moveToolbarButtonToHost(downloadButton, sectionHeaderHost, 'revenue-mis-title-action-btn');
};

const syncDetailedToolbarButtons = (rootNode, sectionHeaderHost) => {
  if (!rootNode || !sectionHeaderHost) return;

  const sourceButtons = Array.from(rootNode.querySelectorAll('.button-group button'));
  if (sourceButtons.length === 0) return;

  sectionHeaderHost.replaceChildren();

  sourceButtons.forEach((buttonElement) => {
    moveToolbarButtonToHost(buttonElement, sectionHeaderHost, 'revenue-mis-title-action-btn');
  });
};

const RevenueMisPageHeader = React.memo(({ actionsHostRef }) => (
  <div className="revenue-mis-page-header">
    <span>Revenue MIS</span>
    <div ref={actionsHostRef} className="revenue-mis-page-header-actions" />
  </div>
));

RevenueMisPageHeader.displayName = 'RevenueMisPageHeader';

RevenueMisPageHeader.propTypes = {
  actionsHostRef: PropTypes.shape({ current: PropTypes.any }),
};

RevenueMisPageHeader.defaultProps = {
  actionsHostRef: null,
};

const RevenueMisSectionTitleBar = React.memo(({ title, actionsHostRef }) => (
  <div className="revenue-mis-section-title-bar">
    <span>{title}</span>
    <div ref={actionsHostRef} className="revenue-mis-section-title-actions" />
  </div>
));

RevenueMisSectionTitleBar.displayName = 'RevenueMisSectionTitleBar';

RevenueMisSectionTitleBar.propTypes = {
  title: PropTypes.string.isRequired,
  actionsHostRef: PropTypes.shape({ current: PropTypes.any }),
};

RevenueMisSectionTitleBar.defaultProps = {
  actionsHostRef: null,
};

const DETAILED_SELECTOR_BUTTON_STYLE = [
  'display:inline-flex',
  'align-items:center',
  'justify-content:center',
  'width:32px',
  'height:32px',
  'padding:0',
  'border:0',
  'background:transparent',
  'cursor:pointer',
].join(';');

const DETAILED_ACTION_BUTTON_STYLE = [
  'display:inline-flex',
  'align-items:center',
  'justify-content:center',
  'width:24px',
  'height:24px',
  'padding:0',
  'border:0',
  'background:transparent',
  'cursor:pointer',
].join(';');

const DETAILED_ACTION_ICON_STYLE = 'width:16px;height:16px;';

const DETAILED_ACTION_CONTAINER_STYLE = [
  'display:inline-flex',
  'align-items:center',
  'justify-content:space-between',
  'gap:28px',
  'width:82px',
].join(';');

const createDetailedSelectorMarkup = (isChecked, isDisabled = false) => {
  const ariaLabel = isDisabled
    ? 'Selection disabled for invoice row'
    : `${isChecked ? 'Deselect' : 'Select'} revenue row`;

  return `
    <button
      type="button"
      class="revenue-mis-detailed-select-btn ${isChecked ? 'is-checked' : ''} ${isDisabled ? 'is-disabled' : ''}"
      aria-label="${ariaLabel}"
      aria-disabled="${isDisabled ? 'true' : 'false'}"
      data-selector-button="true"
      style="${DETAILED_SELECTOR_BUTTON_STYLE}"
      ${isDisabled ? 'disabled' : ''}
    ></button>
  `;
};

const createDetailedHeaderSelectorMarkup = (isAllSelected) => `
  <button
    type="button"
    class="revenue-mis-detailed-select-btn revenue-mis-detailed-select-all ${isAllSelected ? 'is-checked' : ''}"
    aria-label="${isAllSelected ? 'Deselect all revenue rows' : 'Select all revenue rows'}"
    data-selector-all-button="true"
    style="${DETAILED_SELECTOR_BUTTON_STYLE}"
  ></button>
`;

const createDetailedActionMarkup = ({
  showEdit = true,
  showDelete = true,
  disableEdit = false,
  disableDelete = false,
} = {}) => `
  <div
    class="revenue-mis-detailed-action-icons"
    style="${DETAILED_ACTION_CONTAINER_STYLE}"
  >
    ${showEdit ? `
    <button
      type="button"
      class="revenue-mis-detailed-action-btn ${disableEdit ? 'is-disabled' : ''}"
      aria-label="${disableEdit ? 'Edit disabled for this revenue row' : 'Edit revenue row'}"
      aria-disabled="${disableEdit ? 'true' : 'false'}"
      data-row-action="edit"
      style="${DETAILED_ACTION_BUTTON_STYLE}"
      title="${disableEdit ? 'This row cannot be edited' : 'Edit revenue row'}"
      ${disableEdit ? 'disabled' : ''}
    >
      <img
        src="${editIcon}"
        alt=""
        class="revenue-mis-detailed-action-icon"
        style="${DETAILED_ACTION_ICON_STYLE}"
      />
    </button>
    ` : ''}
    ${showDelete ? `
    <button
      type="button"
      class="revenue-mis-detailed-action-btn ${disableDelete ? 'is-disabled' : ''}"
      aria-label="${disableDelete ? 'Delete disabled for this revenue row' : 'Delete revenue row'}"
      aria-disabled="${disableDelete ? 'true' : 'false'}"
      data-row-action="delete"
      style="${DETAILED_ACTION_BUTTON_STYLE}"
      title="${disableDelete ? 'This row cannot be deleted' : 'Delete revenue row'}"
      ${disableDelete ? 'disabled' : ''}
    >
      <img
        src="${deleteIcon}"
        alt=""
        class="revenue-mis-detailed-action-icon"
        style="${DETAILED_ACTION_ICON_STYLE}"
      />
    </button>
    ` : ''}
  </div>
`;

// FIX: Track which rows have already had action cells written so we don't
// rewrite innerHTML on every MutationObserver tick — this was the biggest
// source of lag during filter apply.
const applyDetailedRevenueMisEnhancements = (
  rootNode,
  detailedTableData,
  selectedRowIds,
  permissionFlags,
  onToggleRowSelection,
  onToggleAllRowSelection,
  onOpenDeleteDialog,
  onOpenAccrualDialog,
) => {
  const detailedRootNode = rootNode?.matches?.('.revenue-mis-detailed-table-utility')
    ? rootNode
    : rootNode?.querySelector('.revenue-mis-detailed-table-utility');
  const bodyRows = detailedRootNode?.querySelectorAll('.p-datatable-tbody > tr');
  if (!detailedRootNode || !bodyRows?.length) return;

  const headerCells = detailedRootNode.querySelectorAll('.p-datatable-thead > tr > th');
  const selectorHeaderCell = headerCells?.[0];
  const visibleRowIds = [];
  const canDelete = Boolean(permissionFlags?.canDelete);
  const canUpdate = Boolean(permissionFlags?.canUpdate);

  const wideHeaderWidthMap = new Map([
    ['Budgeted FX rate ( USD to INR)', '260px'],
    ['Zoho Invoice No/ JV Number', '240px'],
    ['Invoiced/Accrued Zoho Client Name', '320px'],
  ]);

  // FIX: Only set column widths once per header render — guard with a data attr
  if (!detailedRootNode.hasAttribute('data-widths-applied')) {
    detailedRootNode.setAttribute('data-widths-applied', '1');
    headerCells.forEach((headerCell, columnIndex) => {
      const headerText = (headerCell.textContent || '').trim();
      const targetWidth = wideHeaderWidthMap.get(headerText);
      if (!targetWidth) return;

      headerCell.style.setProperty('width', targetWidth, 'important');
      headerCell.style.setProperty('min-width', targetWidth, 'important');
      headerCell.style.setProperty('max-width', targetWidth, 'important');
      bodyRows.forEach((rowElement) => {
        const bodyCell = rowElement.querySelectorAll('td')?.[columnIndex];
        bodyCell?.style.setProperty('width', targetWidth, 'important');
        bodyCell?.style.setProperty('min-width', targetWidth, 'important');
        bodyCell?.style.setProperty('max-width', targetWidth, 'important');
      });
    });
  }

  // Collect visible row IDs
  bodyRows.forEach((rowElement, rowIndex) => {
    const rowData = detailedTableData[rowIndex];
    if (isRevenueMisSummaryRow(rowData)) {
      rowElement.removeAttribute('data-rm-row-id');
      return;
    }
    const rowId = rowData?.__rowId || '';
    if (rowId && isUploadedRevenueMisRow(rowData)) {
      visibleRowIds.push(rowId);
    }
    if (rowId) {
      rowElement.setAttribute('data-rm-row-id', rowId);
    }
  });

  const isAllSelected = visibleRowIds.length > 0
    && visibleRowIds.every((rowId) => selectedRowIds.has(rowId));
  const visibleRowIdsFingerprint = visibleRowIds.join('|');

  // FIX: Only rewrite header selector if checked state actually changed
  if (selectorHeaderCell && canDelete) {
    const currentlyChecked = selectorHeaderCell.classList.contains('is-checked');
    const previousVisibleRowIdsFingerprint = selectorHeaderCell.getAttribute('data-visible-row-ids') || '';
    if (
      currentlyChecked !== isAllSelected
      || !selectorHeaderCell.hasAttribute('data-selector-init')
      || previousVisibleRowIdsFingerprint !== visibleRowIdsFingerprint
    ) {
      selectorHeaderCell.setAttribute('data-selector-init', '1');
      selectorHeaderCell.setAttribute('data-visible-row-ids', visibleRowIdsFingerprint);
      selectorHeaderCell.classList.add('revenue-mis-detailed-selector-header-cell');
      selectorHeaderCell.classList.toggle('is-checked', isAllSelected);
      selectorHeaderCell.innerHTML = createDetailedHeaderSelectorMarkup(isAllSelected);
      selectorHeaderCell.onclick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        onToggleAllRowSelection(visibleRowIds);
      };
      const selectAllBtn = selectorHeaderCell.querySelector('[data-selector-all-button="true"]');
      if (selectAllBtn) {
        selectAllBtn.onclick = (event) => {
          event.preventDefault();
          event.stopPropagation();
          onToggleAllRowSelection(visibleRowIds);
        };
      }
    }
  }

  bodyRows.forEach((rowElement, rowIndex) => {
    const rowData = detailedTableData[rowIndex] || {};
    const isSummaryRow = isRevenueMisSummaryRow(rowData);
    const rowId = rowElement.getAttribute('data-rm-row-id') || '';
    const cells = rowElement.querySelectorAll('td');
    const selectorCell = canDelete ? cells[0] : null;
    const actionCell = cells[cells.length - 1];

    if (isSummaryRow) {
      selectorCell?.replaceChildren();
      actionCell?.replaceChildren();
      if (selectorCell) {
        selectorCell.onclick = null;
        selectorCell.classList.remove('revenue-mis-detailed-selector-cell', 'is-checked', 'is-disabled');
      }
      if (actionCell) {
        actionCell.onclick = null;
        actionCell.classList.remove('revenue-mis-detailed-actions-cell');
      }
      return;
    }

    const fallbackRowId = rowData?.__rowId || '';
    const selectorRowId = rowId || fallbackRowId;
    const isCancelledRow = isCancelledRevenueMisRow(rowData);
    const isUploadedRow = isUploadedRevenueMisRow(rowData);
    const disableEdit = isCancelledRow || !isUploadedRow;
    const disableDelete = isCancelledRow || !isUploadedRow;

    // FIX: Only rewrite selector cell innerHTML if check state changed
    if (selectorCell && canDelete) {
      const wasChecked = selectorCell.classList.contains('is-checked');
      const nowChecked = selectorRowId && isUploadedRow ? selectedRowIds.has(selectorRowId) : false;
      const boundRowId = selectorCell.getAttribute('data-bound-row-id') || '';
      const wasDisabled = selectorCell.classList.contains('is-disabled');
      if (
        wasChecked !== nowChecked
        || wasDisabled !== !isUploadedRow
        || !selectorCell.hasAttribute('data-selector-init')
        || boundRowId !== selectorRowId
      ) {
        selectorCell.setAttribute('data-selector-init', '1');
        selectorCell.setAttribute('data-bound-row-id', selectorRowId);
        selectorCell.classList.add('revenue-mis-detailed-selector-cell');
        selectorCell.classList.toggle('is-checked', nowChecked);
        selectorCell.classList.toggle('is-disabled', !isUploadedRow);
        selectorCell.innerHTML = createDetailedSelectorMarkup(nowChecked, !isUploadedRow);
        selectorCell.onclick = (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (!isUploadedRow) return;
          onToggleRowSelection(selectorRowId);
        };
        const selectorBtn = selectorCell.querySelector('[data-selector-button="true"]');
        if (selectorBtn) {
          selectorBtn.onclick = (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (!isUploadedRow) return;
            onToggleRowSelection(selectorRowId);
          };
        }
      }
    }

    // FIX: Only write action cell once — it doesn't depend on selection state
    const actionRowId = actionCell?.getAttribute('data-action-row-id') || '';
    const actionDisabledState = actionCell?.getAttribute('data-action-disabled') || '';
    const nextActionDisabledState = JSON.stringify({
      canUpdate,
      canDelete,
      disableEdit,
      disableDelete,
    });
    if (
      actionCell
      && (
        !actionCell.hasAttribute('data-action-init')
        || actionRowId !== selectorRowId
        || actionDisabledState !== nextActionDisabledState
      )
    ) {
      actionCell.setAttribute('data-action-init', '1');
      actionCell.setAttribute('data-action-row-id', selectorRowId);
      actionCell.setAttribute('data-action-disabled', nextActionDisabledState);
      actionCell.classList.add('revenue-mis-detailed-actions-cell');
      actionCell.innerHTML = createDetailedActionMarkup({
        showEdit: canUpdate,
        showDelete: canDelete,
        disableEdit,
        disableDelete,
      });
      actionCell.querySelectorAll('[data-row-action]').forEach((btn) => {
        btn.onclick = (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (btn.disabled) return;
          if (btn.getAttribute('data-row-action') === 'edit') {
            onOpenAccrualDialog(selectorRowId);
          }
          if (btn.getAttribute('data-row-action') === 'delete') {
            onOpenDeleteDialog(selectorRowId ? [selectorRowId] : []);
          }
        };
      });
    }
  });
};

// ─── Filter helpers ──────────────────────────────────────────────────────────

const toFilterOptionLabel = (option) => {
  if (option && typeof option === 'object') return option.label ?? option.name ?? option.value ?? '';
  return option ?? '';
};

const toFilterOptionValue = (option) => {
  if (option && typeof option === 'object') return option.value ?? option.label ?? option.name ?? '';
  return option ?? '';
};

const normalizeRevenueMisFilterItem = (item = {}) => {
  if (item?.type !== 'filter') return item;

  const normalizedTemplate = REVENUE_MIS_FILTER_TEMPLATES[item?.field] || item?.template;
  const optionSource = Array.isArray(item?.options) && item.options.length > 0
    ? item.options
    : Array.isArray(item?.values) && item.values.length > 0
      ? item.values
      : [];

  if (optionSource.length === 0) {
    return {
      ...item,
      ...(normalizedTemplate ? { template: normalizedTemplate } : {}),
      ...(normalizedTemplate === 'singleSelect'
        ? { selectedValue: item?.selectedValue ?? null, selectedLabel: item?.selectedLabel ?? '' }
        : {}),
    };
  }

  const normalizedOptions = optionSource
    .map((option) => ({ label: toFilterOptionLabel(option), value: toFilterOptionValue(option) }))
    .filter((option) => option.label || option.value);

  normalizedOptions.sort((optionA, optionB) => (
    optionA.label.localeCompare(optionB.label, undefined, { sensitivity: 'base', numeric: true })
  ));

  return {
    ...item,
    ...(normalizedTemplate ? { template: normalizedTemplate } : {}),
    options: normalizedOptions,
    values: normalizedOptions,
    ...(normalizedTemplate === 'singleSelect'
      ? { selectedValue: item?.selectedValue ?? null, selectedLabel: item?.selectedLabel ?? '' }
      : {}),
  };
};

const handleFilteredApiResponse = (response) => {
  if (!response || typeof response !== 'object') return response;

  const responseData = response.data && typeof response.data === 'object'
    ? response.data
    : {};
  const nestedResponseData = responseData.data && typeof responseData.data === 'object'
    ? responseData.data
    : {};
  let rawTableConfiguration = [];
  const bootstrapFilterOptions = responseData?.filterOptions
    || nestedResponseData?.filterOptions
    || null;
  const fallbackTableConfiguration = buildRevenueMisTableConfig(bootstrapFilterOptions || {});

  if (Array.isArray(responseData?.tableConfiguration)) {
    rawTableConfiguration = responseData.tableConfiguration;
  } else if (Array.isArray(nestedResponseData?.tableConfiguration)) {
    rawTableConfiguration = nestedResponseData.tableConfiguration;
  } else if (Array.isArray(response.tableConfiguration)) {
    rawTableConfiguration = response.tableConfiguration;
  } else if (Array.isArray(response.tableConfig)) {
    rawTableConfiguration = response.tableConfig;
  } else if (bootstrapFilterOptions && typeof bootstrapFilterOptions === 'object') {
    rawTableConfiguration = fallbackTableConfiguration;
  }

  rawTableConfiguration = mergeRevenueMisTableConfiguration(
    rawTableConfiguration,
    fallbackTableConfiguration,
  );

  const rawTableData = (
    responseData?.tableData
    || nestedResponseData?.tableData
    || response.tableData
    || []
  );
  const rawSummary = responseData?.summary
    || nestedResponseData?.summary
    || response?.summary
    || null;
  const normalizedTableData = rawTableData
    .map(normalizeRevenueMisRow)
    .filter((row) => !isRevenueMisSummaryRow(row));
  const normalizedSummary = buildRevenueMisTotalRow(rawSummary);
  const tableDataWithSummary = normalizedSummary
    ? [...normalizedTableData, normalizedSummary]
    : normalizedTableData;
  const rawPagination = responseData?.pagination
    || responseData?.paginationConfig
    || nestedResponseData?.pagination
    || nestedResponseData?.paginationConfig
    || response?.meta
    || response?.pagination
    || response?.paginationConfig
    || {};
  const normalizedPagination = {
    page: Number(rawPagination?.page) > 0
      ? Number(rawPagination.page)
      : DEFAULT_REVENUE_MIS_PAGINATION.page,
    limit: Number(rawPagination?.limit ?? rawPagination?.pageSize) > 0
      ? Number(rawPagination?.limit ?? rawPagination?.pageSize)
      : DEFAULT_REVENUE_MIS_PAGINATION.limit,
    total: Number(rawPagination?.total ?? rawPagination?.count) >= 0
      ? Number(rawPagination?.total ?? rawPagination?.count)
      : normalizedTableData.length,
  };

  response.data = {
    ...responseData,
    tableConfiguration: rawTableConfiguration.map((column) => {
      const normalizedColumn = applyRevenueMisNumericAlignment({
        ...column,
        sortable: false,
        isGlobalFilter: column?.isGlobalFilter ?? false,
      });

      if (Array.isArray(column?.menuItems)) {
        normalizedColumn.menuItems = column.menuItems
          .map((menuItem) => {
            const normalizedItems = Array.isArray(menuItem?.items)
              ? menuItem.items
                .filter((item) => item?.type !== 'sort')
                .map(normalizeRevenueMisFilterItem)
              : [];
            return {
              ...menuItem,
              items: normalizedItems,
            };
          })
          .filter((menuItem) => menuItem.items.length > 0);
      } else {
        normalizedColumn.menuItems = [];
      }

      return normalizedColumn;
    }),
    tableData: tableDataWithSummary,
    summary: normalizedSummary,
    meta: normalizedPagination,
    pagination: normalizedPagination,
    paginationConfig: normalizedPagination,
    data: {
      ...nestedResponseData,
      tableConfiguration: rawTableConfiguration,
      tableData: tableDataWithSummary,
      summary: normalizedSummary,
      meta: normalizedPagination,
      pagination: normalizedPagination,
      paginationConfig: normalizedPagination,
    },
  };

  response.meta = {
    ...(response.meta || {}),
    ...normalizedPagination,
  };
  response.pagination = normalizedPagination;
  response.paginationConfig = normalizedPagination;

  normalizeTableUtilityResponse(response);
  return response;
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const RevenueMisActionsMenu = ({
  actionsMenuAnchorElement,
  canCreate,
  onCloseActionsMenu,
  onUploadRevenue,
}) => (
  <Menu
    anchorEl={actionsMenuAnchorElement}
    open={Boolean(actionsMenuAnchorElement)}
    onClose={onCloseActionsMenu}
    disableScrollLock
    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    classes={{ paper: 'revenue-mis-actions-menu' }}
  >
    {canCreate && (
      <MenuItem
        className="revenue-mis-actions-menu-item"
        onClick={() => {
          onCloseActionsMenu();
          onUploadRevenue();
        }}
      >
        Upload Revenue
      </MenuItem>
    )}
  </Menu>
);

const RevenueMisInitialState = ({ animationContainer }) => (
  <div className="revenue-mis-initial-state">
    <div ref={animationContainer} className="revenue-mis-initial-animation" />
    <p className="revenue-mis-initial-text">Select the filters above to see the results</p>
  </div>
);

const RevenueMisDeleteDialog = ({ isOpen, onClose, onConfirm }) => (
  <Dialog
    open={isOpen}
    onClose={onClose}
    classes={{ paper: 'revenue-mis-delete-dialog-paper' }}
  >
    <DialogTitle className="revenue-mis-delete-dialog-title">Delete Records?</DialogTitle>
    <DialogContent className="revenue-mis-delete-dialog-content">
      <div className="revenue-mis-delete-dialog-message">
        <img src={alertIcon} alt="" className="revenue-mis-delete-dialog-alert-icon" />
        <span>Are you sure you want to delete this selected records?</span>
      </div>
    </DialogContent>
    <DialogActions className="revenue-mis-delete-dialog-actions">
      <button type="button" className="revenue-mis-delete-dialog-cancel-btn" onClick={onClose}>Cancel</button>
      <button type="button" className="revenue-mis-delete-dialog-delete-btn" onClick={onConfirm}>Delete</button>
    </DialogActions>
  </Dialog>
);

const RevenueMisAccrualDialog = ({
  isOpen,
  mode,
  formData,
  options,
  isOptionsLoading,
  isSubmitting,
  onChange,
  onClose,
  onSubmit,
}) => {
  const isEditMode = mode === 'edit';
  const title = isEditMode ? 'Edit Accruals' : 'Add Accruals';
  const submitLabel = isEditMode ? 'Update' : 'Add';
  const accrualDateInputRef = useRef(null);
  const handleOpenAccrualDatePicker = useCallback(() => {
    const dateInput = accrualDateInputRef.current;
    if (!dateInput) return;

    if (typeof dateInput.showPicker === 'function') {
      dateInput.showPicker();
      return;
    }

    dateInput.focus();
    dateInput.click();
  }, []);
  const projectOptions = Array.isArray(options?.projects) ? options.projects : [];
  const revenueTypeOptions = Array.isArray(options?.revenueTypes) && options.revenueTypes.length > 0
    ? options.revenueTypes
    : [
      { label: 'Accrual', value: 'Accrual' },
      { label: 'Reversal', value: 'Reversal' },
      { label: 'Credit Note', value: 'Credit Note' },
      { label: 'Cost', value: 'Cost' },
      { label: 'Deferred Revenue', value: 'Deferred Revenue' },
    ];
  const selectedProject = projectOptions.find((project) => (
    String(project.value) === String(formData.projectId)
    || String(project.projectId) === String(formData.projectId)
    || String(project.projectDbId) === String(formData.projectId)
  ));
  const milestoneOptions = (Array.isArray(options?.milestones) ? options.milestones : [])
    .filter((milestone) => {
      if (!formData.projectId || !selectedProject) return true;

      return String(milestone.projectId) === String(selectedProject.projectId || selectedProject.value)
        || String(milestone.projectDbId) === String(selectedProject.projectDbId);
    });
  const accruedEntityOptions = selectedProject?.billingEntity
    ? [{ label: selectedProject.billingEntity, value: selectedProject.billingEntity }]
    : (Array.isArray(options?.accruedEntities) ? options.accruedEntities : []);
  const isDependentSelectDisabled = isOptionsLoading;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      classes={{ paper: 'revenue-mis-accrual-dialog-paper' }}
    >
      <DialogTitle className="revenue-mis-accrual-dialog-title">{title}</DialogTitle>
      <DialogContent className="revenue-mis-accrual-dialog-content">
        <div className="revenue-mis-accrual-form-grid">
          <label className="revenue-mis-accrual-field">
            <span>JV. No.*</span>
            <input
              value={formData.jvNumber}
              placeholder="Enter JV.No."
              onChange={(event) => onChange('jvNumber', event.target.value)}
            />
          </label>
          <label className="revenue-mis-accrual-field">
            <span>Project Name*</span>
            <select
              className={formData.projectId ? 'has-value' : ''}
              value={formData.projectId}
              onChange={(event) => onChange('projectId', event.target.value)}
              disabled={isOptionsLoading}
            >
              <option value="">{isOptionsLoading ? 'Loading...' : 'Select'}</option>
              {projectOptions.map((project) => (
                <option key={`${project.value}-${project.label}`} value={project.value}>
                  {project.label}
                </option>
              ))}
            </select>
          </label>
          <label className="revenue-mis-accrual-field">
            <span>Revenue Type*</span>
            <select
              className={formData.revenueType ? 'has-value' : ''}
              value={formData.revenueType}
              onChange={(event) => onChange('revenueType', event.target.value)}
              disabled={isOptionsLoading}
            >
              <option value="">{isOptionsLoading ? 'Loading...' : 'Select'}</option>
              {revenueTypeOptions.map((revenueType) => (
                <option key={revenueType.value} value={revenueType.value}>
                  {revenueType.label}
                </option>
              ))}
            </select>
          </label>
          <label className="revenue-mis-accrual-field">
            <span>Accrual/Reversal Date*</span>
            <div className="revenue-mis-date-input-shell">
              <input
                ref={accrualDateInputRef}
                type="date"
                value={formData.accrualDate}
                onChange={(event) => onChange('accrualDate', event.target.value)}
              />
              <button
                type="button"
                className="revenue-mis-calendar-button"
                aria-label="Open accrual date picker"
                onClick={handleOpenAccrualDatePicker}
              >
                <img src={calendarIcon} alt="" className="revenue-mis-calendar-icon" />
              </button>
            </div>
          </label>
          <label className="revenue-mis-accrual-field">
            <span>Accrued Entity*</span>
            <select
              className={formData.accruedEntity ? 'has-value' : ''}
              value={formData.accruedEntity}
              onChange={(event) => onChange('accruedEntity', event.target.value)}
              disabled={isDependentSelectDisabled}
            >
              <option value="">{isOptionsLoading ? 'Loading...' : 'Select'}</option>
              {accruedEntityOptions.map((entity) => (
                <option key={entity.value} value={entity.value}>
                  {entity.label}
                </option>
              ))}
            </select>
          </label>
          <label className="revenue-mis-accrual-field">
            <span>Milestone ID*</span>
            <select
              className={formData.milestoneId ? 'has-value' : ''}
              value={formData.milestoneId}
              onChange={(event) => onChange('milestoneId', event.target.value)}
              disabled={isDependentSelectDisabled}
            >
              <option value="">{isOptionsLoading ? 'Loading...' : 'Select'}</option>
              {milestoneOptions.map((milestone) => (
                <option key={`${milestone.projectId}-${milestone.value}`} value={milestone.value}>
                  {milestone.label}
                </option>
              ))}
            </select>
          </label>
          <label className="revenue-mis-accrual-field">
            <span>Accrued/ Reversed Amt(INR)*</span>
            <input
              value={formData.amountInr}
              placeholder="Enter Accrued/ Reversed Amt(INR)"
              onChange={(event) => onChange('amountInr', event.target.value)}
            />
          </label>
          <label className="revenue-mis-accrual-field">
            <span>Revenue Remarks</span>
            <input
              value={formData.remarks}
              placeholder="Enter..."
              onChange={(event) => onChange('remarks', event.target.value)}
            />
          </label>
        </div>
      </DialogContent>
      <DialogActions className="revenue-mis-accrual-dialog-actions">
        <button type="button" className="revenue-mis-accrual-cancel-btn" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </button>
        <button type="button" className="revenue-mis-accrual-submit-btn" onClick={onSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : submitLabel}
        </button>
      </DialogActions>
    </Dialog>
  );
};

const RevenueMisUploadDialog = ({
  isOpen,
  selectedFileName,
  isSubmitting,
  onFileChange,
  onDownloadSample,
  onClose,
  onSubmit,
}) => {
  const fileInputRef = useRef(null);

  const dialogHeader = (
    <span className="p-dialog-title">Upload Revenue</span>
  );

  const dialogFooter = (
    <div className="zoho-upload-footer">
      <PrimeReactButton
        label="Cancel"
        className="p-button p-button-text"
        onClick={onClose}
        disabled={isSubmitting}
      />
      <PrimeReactButton
        label="Upload"
        className="p-button p-button-add"
        onClick={onSubmit}
        disabled={isSubmitting}
        loading={isSubmitting}
      />
    </div>
  );

  return (
    <PrimeReactDialog
      visible={isOpen}
      onHide={onClose}
      header={dialogHeader}
      footer={dialogFooter}
      className="zoho-invoice-dialog"
      style={{ width: '480px' }}
      modal
      draggable={false}
      resizable={false}
    >
      <div className="zoho-upload-body">
        <div className="zoho-upload-field-label">
          Select file to upload
          <span className="required-asterisk">*</span>
        </div>

        <div className="zoho-upload-file-row">
          <PrimeReactButton
            type="button"
            label="Choose file"
            className="p-button zoho-upload-choose-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={isSubmitting}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: 'none' }}
            onChange={onFileChange}
          />
          <span className="zoho-upload-filename">
            {selectedFileName ? selectedFileName : 'No file chosen'}
          </span>
          <button
            type="button"
            onClick={onDownloadSample}
            className="zoho-upload-sample-link"
          >
            Sample File
          </button>
        </div>

        <div className="zoho-upload-hint">
          Accepted formats: xlsx, xls, csv &nbsp;
        </div>
      </div>
    </PrimeReactDialog>
  );
};

const RevenueMisUploadReplaceDialog = ({
  isOpen,
  duplicateRows,
  isSubmitting,
  description,
  showRowNumbers,
  onClose,
  onConfirm,
}) => {
  const formatDuplicateAmount = (value) => {
    const numericValue = typeof value === 'number'
      ? value
      : Number(
        String(value || '')
          .replace(/,/g, '')
          .trim(),
      );
    return Number.isFinite(numericValue) ? String(numericValue) : String(value || '0');
  };

  const getDuplicateRowNumber = (row, index) => (
    readFirstPresent(row, 'excelRowNumber', 'rowNumber', 'uploadRowNumber') || index + 1
  );

  const normalizedDuplicateRows = duplicateRows.reduce((accumulator, row, index) => {
    const normalizedRow = {
      ...row,
      duplicateRowNumber: getDuplicateRowNumber(row, index),
      formattedAmount: formatDuplicateAmount(row.amount),
    };
    const duplicateKey = [
      normalizedRow.duplicateRowNumber,
      normalizedRow.jvNo,
      normalizedRow.milestoneId,
      normalizedRow.formattedAmount,
      normalizedRow.firstRowNumber,
    ].join('|');

    if (!accumulator.some((item) => item.duplicateKey === duplicateKey)) {
      accumulator.push({ ...normalizedRow, duplicateKey });
    }

    return accumulator;
  }, []);

  const warningType = useMemo(() => {
    if (duplicateRows.some((rowItem) => rowItem.isZeroAmount)) return 'zeroAmount';
    if (duplicateRows.some((rowItem) => rowItem.isInFileDuplicate)) return 'inFileDuplicate';
    return 'dbDuplicate';
  }, [duplicateRows]);

  const dialogTitle = useMemo(() => {
    if (warningType === 'zeroAmount') return 'Zero Amount Warning';
    if (warningType === 'inFileDuplicate') return 'Duplicate Entries in Uploaded File';
    return 'Replace Existing Records?';
  }, [warningType]);

  const defaultDescription = useMemo(() => {
    if (!showRowNumbers) {
      return 'This entry already exists. Do you want to replace it?';
    }
    if (warningType === 'zeroAmount') {
      return 'Some rows in the uploaded file have an amount of 0. Do you want to continue?';
    }
    if (warningType === 'inFileDuplicate') {
      return 'The uploaded file contains duplicate rows with different amounts. Do you want to continue?';
    }
    return 'Some uploaded entries already exist. Do you want to replace them?';
  }, [showRowNumbers, warningType]);

  const confirmButtonLabel = useMemo(() => {
    if (isSubmitting) return 'Processing...';
    if (warningType === 'zeroAmount' || warningType === 'inFileDuplicate') return 'Continue';
    return 'Replace';
  }, [isSubmitting, warningType]);

  const renderRowText = (row) => {
    const jvVal = row.jvNo || 'NA';
    const mileVal = row.milestoneId || 'NA';
    const typeVal = row.revenueType || 'Accrual';

    if (row.isZeroAmount) {
      const zeroDetail = `JV: ${jvVal}, Milestone: ${mileVal} (${typeVal})`;
      return `Row ${row.duplicateRowNumber}: Amount is 0 for ${zeroDetail}.`;
    }
    if (row.isInFileDuplicate) {
      const rowLabel = `Row ${row.firstRowNumber} and Row ${row.duplicateRowNumber}`;
      const detailLabel = `JV: ${jvVal}, Milestone: ${mileVal} (${typeVal})`;
      const amountLabel = `Amounts: ${row.firstAmount} vs ${row.duplicateAmount}`;
      return `${rowLabel}: Duplicate entry for ${detailLabel}. ${amountLabel}.`;
    }
    const dbDetail = `JV: ${jvVal}, Milestone: ${mileVal} (${typeVal})`;
    return `Row ${row.duplicateRowNumber}: ${dbDetail} with Amount ${row.formattedAmount} already exists.`;
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      classes={{ paper: 'revenue-mis-delete-dialog-paper revenue-mis-replace-dialog-paper' }}
    >
      <DialogTitle className="revenue-mis-delete-dialog-title">{dialogTitle}</DialogTitle>
      <DialogContent className="revenue-mis-delete-dialog-content">
        <div className="revenue-mis-delete-dialog-message">
          <div className="revenue-mis-replace-dialog-intro">
            <img src={alertIcon} alt="" className="revenue-mis-delete-dialog-alert-icon" />
            <span>{description || defaultDescription}</span>
          </div>
          <div className="revenue-mis-replace-dialog-duplicate-list">
            {normalizedDuplicateRows.map((row) => (
              <div
                key={row.duplicateKey}
                className="revenue-mis-replace-dialog-duplicate-item"
              >
                {renderRowText(row)}
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
      <DialogActions className="revenue-mis-delete-dialog-actions">
        <button
          type="button"
          className="revenue-mis-delete-dialog-cancel-btn"
          onClick={onClose}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="revenue-mis-delete-dialog-delete-btn"
          onClick={onConfirm}
          disabled={isSubmitting}
        >
          {confirmButtonLabel}
        </button>
      </DialogActions>
    </Dialog>
  );
};

RevenueMisActionsMenu.propTypes = {
  actionsMenuAnchorElement: PropTypes.oneOfType([
    PropTypes.instanceOf(Element),
    PropTypes.instanceOf(HTMLButtonElement),
  ]),
  canCreate: PropTypes.bool.isRequired,
  onCloseActionsMenu: PropTypes.func.isRequired,
  onUploadRevenue: PropTypes.func.isRequired,
};
RevenueMisActionsMenu.defaultProps = { actionsMenuAnchorElement: null };

RevenueMisInitialState.propTypes = {
  animationContainer: PropTypes.shape({ current: PropTypes.instanceOf(Element) }).isRequired,
};

RevenueMisDeleteDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

RevenueMisUploadReplaceDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  duplicateRows: PropTypes.arrayOf(PropTypes.shape({
    rowNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    uploadRowNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    jvNo: PropTypes.string,
    milestoneId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  })).isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  description: PropTypes.string,
  showRowNumbers: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

RevenueMisUploadReplaceDialog.defaultProps = {
  description: 'Some uploaded entries already exist. Do you want to replace them?',
  showRowNumbers: false,
};

RevenueMisAccrualDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['add', 'edit']).isRequired,
  formData: PropTypes.shape({
    jvNumber: PropTypes.string,
    projectId: PropTypes.string,
    revenueType: PropTypes.string,
    accrualDate: PropTypes.string,
    accruedEntity: PropTypes.string,
    milestoneId: PropTypes.string,
    amountInr: PropTypes.string,
    remarks: PropTypes.string,
  }).isRequired,
  options: PropTypes.shape({
    projects: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
      projectId: PropTypes.string,
      projectDbId: PropTypes.string,
      billingEntity: PropTypes.string,
    })),
    revenueTypes: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })),
    accruedEntities: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })),
    milestones: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
      projectId: PropTypes.string,
      projectDbId: PropTypes.string,
    })),
  }),
  isOptionsLoading: PropTypes.bool,
  isSubmitting: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

RevenueMisAccrualDialog.defaultProps = {
  options: {},
  isOptionsLoading: false,
};

RevenueMisUploadDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  selectedFileName: PropTypes.string.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  onFileChange: PropTypes.func.isRequired,
  onDownloadSample: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

// ─── Main component ──────────────────────────────────────────────────────────

const RevenueMisDetailedTableSkeleton = ({ rows, columns }) => (
  <div className="revenue-mis-detailed-skeleton" aria-hidden="true">
    <div className="revenue-mis-detailed-skeleton__header">
      {Array.from({ length: columns }).map((columnValue, columnIndex) => (
        <span
          key={`revenue-mis-detailed-skeleton-header-${columnIndex + 1}`}
          className="revenue-mis-detailed-skeleton__header-cell"
        />
      ))}
    </div>
    <div className="revenue-mis-detailed-skeleton__body">
      {Array.from({ length: rows }).map((rowValue, rowIndex) => (
        <div
          key={`revenue-mis-detailed-skeleton-row-${rowIndex + 1}`}
          className="revenue-mis-detailed-skeleton__row"
        >
          {Array.from({ length: columns }).map((columnValue, columnIndex) => (
            <span
              key={`revenue-mis-detailed-skeleton-cell-${rowIndex + 1}-${columnIndex + 1}`}
              className="revenue-mis-detailed-skeleton__cell"
            />
          ))}
        </div>
      ))}
    </div>
  </div>
);

RevenueMisDetailedTableSkeleton.propTypes = {
  rows: PropTypes.number,
  columns: PropTypes.number,
};

RevenueMisDetailedTableSkeleton.defaultProps = {
  rows: 10,
  columns: 8,
};

const RevenueMIS = () => {
  const { userProfile } = useAuth();
  const { snackbarOpen } = useSnackbar();
  const authToken = userProfile?.access_token || getStoredAuthToken();
  const revenueMisApiUrl = buildProjectServiceApiUrl('/v1/revenue-mis');
  const revenueMisConsolidatedApiUrl = buildProjectServiceApiUrl('/v1/revenue-mis/consolidated');
  const revenueMisDetailedApiUrl = buildProjectServiceApiUrl('/v1/revenue-mis/detailed');
  const revenueMisPermissions = userProfile?.permissions?.[MODULE_NAMES.MIS_REVENUE_MIS] || [];
  const canCreate = revenueMisPermissions.includes('create');
  const canUpdate = revenueMisPermissions.includes('update');
  const canDelete = revenueMisPermissions.includes('delete');

  const [animationData, setAnimationData] = useState(null);
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const [tableScrollHeight, setTableScrollHeight] = useState('400px');
  const [actionsMenuAnchorElement, setActionsMenuAnchorElement] = useState(null);
  const [consolidatedTableData, setConsolidatedTableData] = useState([]);
  const [detailedTableData, setDetailedTableData] = useState([]);
  const [isDetailedTableLoading, setIsDetailedTableLoading] = useState(false);
  const [selectedDetailedRowIds, setSelectedDetailedRowIds] = useState(() => new Set());
  const selectedDetailedRowIdsRef = useRef(new Set());
  const [deleteDialogRowIds, setDeleteDialogRowIds] = useState([]);
  const [accrualDialogState, setAccrualDialogState] = useState({ isOpen: false, mode: 'add', rowId: '' });
  const [accrualDialogFormDraft, setAccrualDialogFormDraft] = useState({
    jvNumber: '',
    projectId: '',
    revenueType: '',
    accrualDate: '',
    accruedEntity: '',
    milestoneId: '',
    amountInr: '',
    remarks: '',
  });
  const [isAccrualSubmitting, setIsAccrualSubmitting] = useState(false);
  const [isAccrualOptionsLoading, setIsAccrualOptionsLoading] = useState(false);
  const [accrualDialogOptions, setAccrualDialogOptions] = useState({
    projects: [],
    revenueTypes: [],
    accruedEntities: [],
    milestones: [],
  });
  const [pendingAccrualEntry, setPendingAccrualEntry] = useState(null);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadFileName, setUploadFileName] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [isUploadSubmitting, setIsUploadSubmitting] = useState(false);
  const [uploadDuplicateRows, setUploadDuplicateRows] = useState([]);
  const [pendingUploadRows, setPendingUploadRows] = useState([]);
  const [consolidatedPagination, setConsolidatedPagination] = useState(DEFAULT_REVENUE_MIS_PAGINATION);
  const [detailedPagination, setDetailedPagination] = useState(DEFAULT_REVENUE_MIS_PAGINATION);

  const animationContainer = useRef(null);
  const tableContainerRef = useRef(null);
  const detailedSectionRef = useRef(null);
  const pageHeaderActionsRef = useRef(null);
  const consolidatedHeaderActionsRef = useRef(null);
  const detailedHeaderActionsRef = useRef(null);
  const tableStateGetterRef = useRef(null);
  const consolidatedPaginationRef = useRef(DEFAULT_REVENUE_MIS_PAGINATION);
  const detailedPaginationRef = useRef(DEFAULT_REVENUE_MIS_PAGINATION);

  // FIX: Store all mutable cross-render state in a single ref to avoid
  // stale-closure bugs and prevent unnecessary re-renders.
  const stateRef = useRef({
    activeFilterParams: null,
    lastFilterFingerprint: '',
    // FIX: Track in-flight detailed fetch to cancel superseded requests
    detailedFetchController: null,
  });

  const initialTableConfig = useMemo(() => buildRevenueMisTableConfig(), []);
  const detailedTableConfig = useMemo(
    () => buildDetailedRevenueMisTableConfig({ canUpdate, canDelete }),
    [canDelete, canUpdate],
  );
  const revenueMisUserInfo = useMemo(() => ({
    emailId: userProfile?.email,
    employeeId: userProfile?.employeeId,
    fullName: userProfile?.user_name,
    permissions: userProfile?.permissions,
    roleDetails: userProfile?.roleDetails,
    globalColors: userProfile?.siteConfiguration?.global_colors,
  }), [
    userProfile?.email,
    userProfile?.employeeId,
    userProfile?.permissions,
    userProfile?.roleDetails,
    userProfile?.siteConfiguration?.global_colors,
    userProfile?.user_name,
  ]);

  const setSyncedSelectedDetailedRowIds = useCallback((updater) => {
    setSelectedDetailedRowIds((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      selectedDetailedRowIdsRef.current = next;
      return next;
    });
  }, []);

  // ── Fetch ALL records for Detailed table ────────────────────────────────
  const fetchAllDetailedData = useCallback(async (filters) => {
    if (!Array.isArray(filters) || filters.length === 0 || !revenueMisDetailedApiUrl) return;

    // FIX: Cancel any previous in-flight fetch before starting a new one.
    // Without this, a slow previous request could overwrite fresh results.
    stateRef.current.detailedFetchController?.abort();
    const controller = new AbortController();
    stateRef.current.detailedFetchController = controller;
    setIsDetailedTableLoading(true);

    try {
      const res = await fetch(revenueMisDetailedApiUrl, {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          token: authToken,
        },
        body: JSON.stringify({
          isDetailedRevenueMis: true,
          filter: filters,
          pagination: { page: 1, limit: 99999 },
          selectedViewId: null,
          searchQuery: '',
          userInfo: revenueMisUserInfo,
        }),
      });

      if (!res.ok) return;

      const json = await res.json();
      const rawRows = json?.data?.tableData
        || json?.data?.data?.tableData
        || json?.tableData
        || [];
      const rawSummary = json?.data?.summary
        || json?.data?.data?.summary
        || json?.summary
        || null;

      const normalizedRows = rawRows
        .map(normalizeRevenueMisRow)
        .filter((row) => !isRevenueMisSummaryRow(row))
        .map((row, index) => {
          const revenueDetailId = readFirstPresent(row, 'revenueDetailId');
          const revenueSourceId = readFirstPresent(row, 'revenueSourceId');
          const revenueRowSource = readFirstPresent(row, 'revenueRowSource') || 'invoice';
          const fallbackRowKey = readFirstPresent(row, 'milestoneId', 'projectId', 'projectName')
            || `revenue-row-${index}`;

          return {
            ...row,
            __rowId: String(
              revenueSourceId
                ? `${revenueRowSource}-${revenueSourceId}`
                : revenueDetailId
                  ? `revenue-detail-${revenueDetailId}`
                  : `${revenueRowSource}-${fallbackRowKey}-${index}`,
            ),
          };
        });
      const summaryRow = buildRevenueMisTotalRow(rawSummary);

      setDetailedTableData(summaryRow ? [...normalizedRows, summaryRow] : normalizedRows);
      const nextDetailedPagination = {
        ...detailedPaginationRef.current,
        total: normalizedRows.length,
      };
      detailedPaginationRef.current = nextDetailedPagination;
      setDetailedPagination((previousPagination) => (
        previousPagination.total === nextDetailedPagination.total
          ? previousPagination
          : nextDetailedPagination
      ));
      setSyncedSelectedDetailedRowIds((prev) => {
        const next = new Set();
        normalizedRows.forEach((row) => {
          if (prev.has(row.__rowId)) next.add(row.__rowId);
        });
        return next;
      });
    } catch (err) {
      // Ignore abort errors (caused by our own cancellation above)
      if (err?.name !== 'AbortError') {
        // Silently ignore other errors — Consolidated table is unaffected.
      }
    } finally {
      if (stateRef.current.detailedFetchController === controller) {
        stateRef.current.detailedFetchController = null;
        setIsDetailedTableLoading(false);
      }
    }
  }, [
    authToken,
    revenueMisDetailedApiUrl,
    setSyncedSelectedDetailedRowIds,
    revenueMisUserInfo,
  ]);

  // ── Consolidated table API response handler ──────────────────────────────
  // FIX: Removed handleTableStateHandler as the sole source of truth for
  // filter changes. This handler is the canonical place — do NOT also trigger
  // fetchAllDetailedData from handleTableStateHandler to avoid double-fetching.
  const handleRevenueMisApiResponse = useCallback((response) => {
    const normalizedResponse = handleFilteredApiResponse(response);
    const consolidatedRows = Array.isArray(normalizedResponse?.data?.tableData)
      ? normalizedResponse.data.tableData.filter((row) => !isRevenueMisSummaryRow(row))
      : [];

    setConsolidatedTableData(consolidatedRows);
    const responseFilters = getAppliedFiltersFromConfiguration(
      normalizedResponse?.data?.tableConfiguration || [],
    );
    const appliedFilters = responseFilters.length > 0
      ? responseFilters
      : stateRef.current.activeFilterParams || [];
    const fingerprint = getFilterFingerprint(appliedFilters);

    const filterChanged = fingerprint !== stateRef.current.lastFilterFingerprint;
    stateRef.current.lastFilterFingerprint = fingerprint;

    if (filterChanged) {
      stateRef.current.activeFilterParams = appliedFilters;
      fetchAllDetailedData(appliedFilters);
    }

    const responsePagination = normalizedResponse?.data?.pagination
      || normalizedResponse?.data?.paginationConfig
      || normalizedResponse?.meta
      || {};
    const nextPagination = {
      page: Number(responsePagination?.page) > 0
        ? Number(responsePagination.page)
        : consolidatedPaginationRef.current.page,
      limit: Number(responsePagination?.limit ?? responsePagination?.pageSize) > 0
        ? Number(responsePagination?.limit ?? responsePagination?.pageSize)
        : consolidatedPaginationRef.current.limit,
      total: Number(responsePagination?.total ?? responsePagination?.count) >= 0
        ? Number(responsePagination?.total ?? responsePagination?.count)
        : consolidatedPaginationRef.current.total,
    };

    consolidatedPaginationRef.current = nextPagination;
    setConsolidatedPagination((previousPagination) => (
      previousPagination.page === nextPagination.page
      && previousPagination.limit === nextPagination.limit
      && previousPagination.total === nextPagination.total
        ? previousPagination
        : nextPagination
    ));

    return normalizedResponse;
  }, [fetchAllDetailedData]);

  const handleConsolidatedPaginationChange = useCallback((nextPagination) => {
    const normalizedPagination = {
      page: Number(nextPagination?.page) > 0
        ? Number(nextPagination.page)
        : DEFAULT_REVENUE_MIS_PAGINATION.page,
      limit: Number(nextPagination?.limit) > 0
        ? Number(nextPagination.limit)
        : DEFAULT_REVENUE_MIS_PAGINATION.limit,
      total: Number(nextPagination?.total) >= 0
        ? Number(nextPagination.total)
        : consolidatedPaginationRef.current.total,
    };

    consolidatedPaginationRef.current = normalizedPagination;
    setConsolidatedPagination((previousPagination) => (
      previousPagination.page === normalizedPagination.page
      && previousPagination.limit === normalizedPagination.limit
      && previousPagination.total === normalizedPagination.total
        ? previousPagination
        : normalizedPagination
    ));
  }, []);

  const handleDetailedPaginationChange = useCallback((nextPagination) => {
    const normalizedPagination = {
      page: Number(nextPagination?.page) > 0
        ? Number(nextPagination.page)
        : DEFAULT_REVENUE_MIS_PAGINATION.page,
      limit: Number(nextPagination?.limit) > 0
        ? Number(nextPagination.limit)
        : DEFAULT_REVENUE_MIS_PAGINATION.limit,
      total: Number(nextPagination?.total) >= 0
        ? Number(nextPagination.total)
        : detailedPaginationRef.current.total,
    };

    detailedPaginationRef.current = normalizedPagination;
    setDetailedPagination((previousPagination) => (
      previousPagination.page === normalizedPagination.page
      && previousPagination.limit === normalizedPagination.limit
      && previousPagination.total === normalizedPagination.total
        ? previousPagination
        : normalizedPagination
    ));
  }, []);

  // ── Upload handlers ──────────────────────────────────────────────────────
  const handleOpenActionsMenu = useCallback((event) => {
    if (!canCreate) return;
    setActionsMenuAnchorElement(event.currentTarget);
  }, [canCreate]);

  const handleCloseActionsMenu = useCallback(() => {
    setActionsMenuAnchorElement(null);
  }, []);

  const handleUploadRevenue = useCallback(() => {
    if (!canCreate) return;
    setIsUploadDialogOpen(true);
  }, [canCreate]);

  const refreshRevenueTable = useCallback(() => {
    const tableState = tableStateGetterRef.current?.();
    if (tableState?.fetchTableData) {
      tableState.fetchTableData(
        tableState.filters,
        tableState.pagination,
        tableState.searchQuery,
      );
    }
  }, []);
  const getCurrentDetailedFilters = useCallback(() => {
    const tableState = tableStateGetterRef.current?.();
    const configFilters = getAppliedFiltersFromConfiguration(tableState?.tableConfiguration || []);
    const stateFilters = Array.isArray(tableState?.filters)
      ? tableState.filters.filter(hasFilterValue)
      : [];
    const storedFilters = Array.isArray(stateRef.current.activeFilterParams)
      ? stateRef.current.activeFilterParams.filter(hasFilterValue)
      : [];

    if (stateFilters.length > 0) return stateFilters;
    if (configFilters.length > 0) return configFilters;
    return storedFilters;
  }, []);
  const refreshRevenueMisData = useCallback(() => {
    refreshRevenueTable();
    const detailedFilters = getCurrentDetailedFilters();
    if (Array.isArray(detailedFilters) && detailedFilters.length > 0) {
      fetchAllDetailedData(detailedFilters);
    }
  }, [fetchAllDetailedData, getCurrentDetailedFilters, refreshRevenueTable]);
  const fetchAccrualDialogOptions = useCallback(async () => {
    if (!revenueMisApiUrl) return;

    setIsAccrualOptionsLoading(true);
    try {
      const response = await fetch(`${revenueMisApiUrl}/entry-options`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          token: authToken,
        },
        body: JSON.stringify({
          userInfo: revenueMisUserInfo,
        }),
      });
      const responseJson = await response.json().catch(() => ({}));

      if (!response.ok || responseJson?.success === false) {
        throw new Error(responseJson?.message || 'Unable to fetch Revenue MIS options.');
      }

      setAccrualDialogOptions({
        projects: Array.isArray(responseJson?.data?.projects) ? responseJson.data.projects : [],
        revenueTypes: Array.isArray(responseJson?.data?.revenueTypes) ? responseJson.data.revenueTypes : [],
        accruedEntities: Array.isArray(responseJson?.data?.accruedEntities) ? responseJson.data.accruedEntities : [],
        milestones: Array.isArray(responseJson?.data?.milestones) ? responseJson.data.milestones : [],
      });
    } catch (error) {
      snackbarOpen(error?.message || 'Unable to fetch Revenue MIS options.', 'error', 6000);
    } finally {
      setIsAccrualOptionsLoading(false);
    }
  }, [authToken, revenueMisApiUrl, revenueMisUserInfo, snackbarOpen]);

  useEffect(() => {
    if (accrualDialogState.isOpen) {
      fetchAccrualDialogOptions();
    }
  }, [accrualDialogState.isOpen, fetchAccrualDialogOptions]);

  const handleDownloadSampleFile = useCallback(async () => {
    try {
      const response = await fetch(`${revenueMisApiUrl}/get-sample-file`, {
        method: 'GET',
        headers: {
          token: authToken,
        },
      });

      const responseJson = await response.json().catch(() => ({}));
      const signedUrl = responseJson?.data?.signedUrl;

      if (!response.ok || !signedUrl) {
        throw new Error(responseJson?.message || 'Failed to download sample file.');
      }

      const link = document.createElement('a');
      link.href = signedUrl;
      link.style.display = 'none';

      document.body.append(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);

      snackbarOpen('Sample file downloaded successfully.', 'success');
    } catch (error) {
      snackbarOpen(error?.message || 'Failed to download sample file.', 'error');
    }
  }, [authToken, revenueMisApiUrl, snackbarOpen]);

  const handleCloseUploadDialog = useCallback(() => {
    if (isUploadSubmitting) return;
    setIsUploadDialogOpen(false);
    setUploadFileName('');
    setUploadFile(null);
  }, [isUploadSubmitting]);
  const handleCloseUploadReplaceDialog = useCallback(() => {
    if (isUploadSubmitting || isAccrualSubmitting) return;
    setUploadDuplicateRows([]);
    setPendingUploadRows([]);
    setPendingAccrualEntry(null);
  }, [isAccrualSubmitting, isUploadSubmitting]);
  const handleUploadFileChange = useCallback((event) => {
    const selectedFile = event.target.files?.[0] || null;
    setUploadFile(selectedFile);
    setUploadFileName(selectedFile?.name || '');
  }, []);
  const submitRevenueUpload = useCallback(async (uploadData, replaceExisting = false) => {
    setIsUploadSubmitting(true);

    try {
      const response = await fetch(`${revenueMisApiUrl}/upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          token: authToken,
        },
        body: JSON.stringify({
          userInfo: revenueMisUserInfo,
          importData: {
            uploadData,
            replaceExisting,
          },
        }),
      });

      const responseJson = await response.json().catch(() => ({}));
      if (responseJson?.data?.requiresConfirmation) {
        const zeroData = responseJson?.data?.zeroAmountRows;
        const inFileDuplicatesData = responseJson?.data?.inFileDuplicates;
        const dbDuplicatesData = responseJson?.data?.duplicates;

        const zeroRows = Array.isArray(zeroData) ? zeroData : [];
        const inFileRows = Array.isArray(inFileDuplicatesData) ? inFileDuplicatesData : [];
        const dbRows = Array.isArray(dbDuplicatesData) ? dbDuplicatesData : [];

        const formattedWarningRows = [
          ...inFileRows.map((item) => ({
            ...item,
            isInFileDuplicate: true,
            rowNumber: item.duplicateRowNumber,
            jvNo: item.jvNo,
            milestoneId: item.milestoneId,
            revenueType: item.revenueType,
            accrualDate: item.accrualDate,
            amount: `${item.firstAmount} vs ${item.duplicateAmount}`,
          })),
          ...zeroRows.map((item) => ({
            ...item,
            isZeroAmount: true,
            rowNumber: item.rowNumber,
            uploadRowNumber: item.rowNumber,
            jvNo: item.jvNo,
            milestoneId: item.milestoneId,
            revenueType: item.revenueType,
            accrualDate: item.accrualDate,
            amount: 0,
          })),
          ...dbRows.map((item) => ({
            ...item,
            isDbDuplicate: true,
          })),
        ];

        setUploadDuplicateRows(formattedWarningRows);
        setPendingUploadRows(uploadData);
        setIsUploadDialogOpen(false);
        return;
      }

      if (!response.ok || responseJson?.success === false) {
        throw new Error(responseJson?.message || 'Revenue upload failed.');
      }

      snackbarOpen(responseJson?.message || 'Revenue MIS file uploaded successfully.', 'success');
      setIsUploadDialogOpen(false);
      setUploadDuplicateRows([]);
      setPendingUploadRows([]);
      setUploadFileName('');
      setUploadFile(null);
      refreshRevenueMisData();
    } catch (error) {
      snackbarOpen(
        error?.message || 'Unable to upload Revenue MIS file.',
        'error',
        6000,
        'revenue-mis-upload-snackbar',
      );
    } finally {
      setIsUploadSubmitting(false);
    }
  }, [
    revenueMisApiUrl,
    snackbarOpen,
    authToken,
    refreshRevenueMisData,
    revenueMisUserInfo,
  ]);
  const handleSubmitUploadDialog = useCallback(async () => {
    if (!uploadFile) {
      snackbarOpen('Please choose a Revenue MIS upload file.', 'warning');
      return;
    }

    try {
      const parsedUploadRows = await parseRevenueUploadFile(uploadFile);
      await submitRevenueUpload(parsedUploadRows, false);
    } catch (error) {
      snackbarOpen(
        error?.message || 'Unable to upload Revenue MIS file.',
        'error',
        6000,
        'revenue-mis-upload-snackbar',
      );
    }
  }, [snackbarOpen, submitRevenueUpload, uploadFile]);
  // ── Row selection handlers ───────────────────────────────────────────────
  const handleToggleDetailedRowSelection = useCallback((rowId) => {
    if (!rowId) return;
    const row = detailedTableData.find((item) => item.__rowId === rowId);
    if (!isUploadedRevenueMisRow(row)) return;

    setSyncedSelectedDetailedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, [detailedTableData, setSyncedSelectedDetailedRowIds]);

  const handleToggleAllDetailedRowSelection = useCallback((rowIds = []) => {
    const uploadedRowIds = rowIds.filter((rowId) => {
      const row = detailedTableData.find((item) => item.__rowId === rowId);
      return isUploadedRevenueMisRow(row);
    });

    setSyncedSelectedDetailedRowIds((prev) => {
      const allSelected = uploadedRowIds.length > 0 && uploadedRowIds.every((id) => prev.has(id));
      return allSelected ? new Set() : new Set(uploadedRowIds);
    });
  }, [detailedTableData, setSyncedSelectedDetailedRowIds]);

  const handleOpenDetailedDeleteDialog = useCallback((rowIds = []) => {
    if (!canDelete) return;
    const selectedRowIds = Array.isArray(rowIds) && rowIds.length > 0
      ? rowIds
      : Array.from(selectedDetailedRowIds);
    const activeRowIds = selectedRowIds.filter((rowId) => {
      const row = detailedTableData.find((item) => item.__rowId === rowId);
      return isUploadedRevenueMisRow(row) && !isCancelledRevenueMisRow(row);
    });
    if (activeRowIds.length === 0) {
      if (selectedRowIds.length > 0) {
        snackbarOpen('Only uploaded Revenue MIS rows can be deleted.', 'warning');
      }
      return;
    }
    setDeleteDialogRowIds(activeRowIds);
  }, [canDelete, detailedTableData, selectedDetailedRowIds, snackbarOpen]);

  const handleCloseDetailedDeleteDialog = useCallback(() => setDeleteDialogRowIds([]), []);

  const handleConfirmDetailedDelete = useCallback(async () => {
    const deleteRows = detailedTableData.filter(
      (row) => (
        deleteDialogRowIds.includes(row.__rowId)
        && isUploadedRevenueMisRow(row)
        && !isCancelledRevenueMisRow(row)
      ),
    );
    const revenueDetailIds = deleteRows
      .map((row) => readFirstPresent(row, 'revenueDetailId'))
      .filter(Boolean);
    const sourceRecords = deleteRows
      .map(getRevenueMisSourceRecord)
      .filter((record) => record.source && record.id);
    const rowKeys = deleteRows
      .filter((row) => readFirstPresent(row, 'revenueRowSource') !== 'invoice')
      .map(getRevenueMisRowKey)
      .filter((rowKey) => rowKey.jvNo && rowKey.milestoneId);

    if (sourceRecords.length === 0 && revenueDetailIds.length === 0 && rowKeys.length === 0) {
      snackbarOpen('Unable to identify the selected Revenue MIS rows for deletion.', 'warning');
      setDeleteDialogRowIds([]);
      return;
    }

    try {
      const response = await fetch(`${revenueMisApiUrl}/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          token: authToken,
        },
        body: JSON.stringify({
          ids: revenueDetailIds,
          records: sourceRecords,
          rowKeys,
          userInfo: revenueMisUserInfo,
        }),
      });
      const responseJson = await response.json().catch(() => ({}));

      if (!response.ok || responseJson?.success === false) {
        throw new Error(responseJson?.message || 'Unable to delete Revenue MIS rows.');
      }

      snackbarOpen(responseJson?.message || 'Revenue MIS rows deleted successfully.', 'success');
      setSyncedSelectedDetailedRowIds((prev) => {
        const next = new Set(prev);
        deleteDialogRowIds.forEach((rowId) => next.delete(rowId));
        return next;
      });
      setDeleteDialogRowIds([]);
      refreshRevenueMisData();
    } catch (error) {
      snackbarOpen(error?.message || 'Unable to delete Revenue MIS rows.', 'error', 6000);
    }
  }, [
    authToken,
    deleteDialogRowIds,
    detailedTableData,
    refreshRevenueMisData,
    revenueMisApiUrl,
    setSyncedSelectedDetailedRowIds,
    snackbarOpen,
    revenueMisUserInfo,
  ]);

  const handleOpenAccrualDialog = useCallback((rowId = '') => {
    if (!rowId && !canCreate) return;
    if (rowId && !canUpdate) return;
    const selectedRow = detailedTableData.find((row) => row.__rowId === rowId) || {};
    if (rowId && !isUploadedRevenueMisRow(selectedRow)) {
      snackbarOpen('Only uploaded Revenue MIS rows can be edited.', 'warning');
      return;
    }
    if (rowId && isCancelledRevenueMisRow(selectedRow)) {
      snackbarOpen('Cancelled Revenue MIS rows cannot be edited.', 'warning');
      return;
    }
    setAccrualDialogFormDraft({
      jvNumber: readFirstPresent(selectedRow, 'zohoInvoiceOrJvNumber'),
      projectId: String(readFirstPresent(selectedRow, 'projectId') || ''),
      revenueType: readFirstPresent(selectedRow, 'revenueType') || 'Accrual',
      accrualDate: parseRevenueUploadDate(readFirstPresent(selectedRow, 'accruedInvoicedDate')),
      accruedEntity: readFirstPresent(selectedRow, 'zohoBillingEntity'),
      milestoneId: String(readFirstPresent(selectedRow, 'milestoneId') || ''),
      amountInr: String(readFirstPresent(selectedRow, 'totalRevenueInr') || '').replace(/,/g, ''),
      remarks: readFirstPresent(selectedRow, 'remarks'),
    });
    setAccrualDialogState({ isOpen: true, mode: rowId ? 'edit' : 'add', rowId });
  }, [canCreate, canUpdate, detailedTableData, snackbarOpen]);

  const handleCloseAccrualDialog = useCallback(() => {
    if (isAccrualSubmitting) return;
    setAccrualDialogState((prev) => ({ ...prev, isOpen: false, rowId: '' }));
  }, [isAccrualSubmitting]);

  const handleChangeAccrualDialogField = useCallback((field, value) => {
    setAccrualDialogFormDraft((prev) => {
      if (field === 'projectId') {
        const selectedProject = (accrualDialogOptions.projects || [])
          .find((project) => String(project.value) === String(value));

        return {
          ...prev,
          projectId: value,
          accruedEntity: selectedProject?.billingEntity || '',
          milestoneId: '',
        };
      }

      return {
        ...prev,
        [field]: value,
      };
    });
  }, [accrualDialogOptions.projects]);

  const submitRevenueAccrualEntry = useCallback(async (entry, replaceExisting = false) => {
    setIsAccrualSubmitting(true);

    try {
      const response = await fetch(`${revenueMisApiUrl}/entry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          token: authToken,
        },
        body: JSON.stringify({
          entry,
          replaceExisting,
          userInfo: revenueMisUserInfo,
        }),
      });
      const responseJson = await response.json().catch(() => ({}));

      if (responseJson?.data?.requiresConfirmation) {
        setUploadDuplicateRows(responseJson?.data?.duplicates || []);
        setPendingAccrualEntry(entry);
        return;
      }

      if (!response.ok || responseJson?.success === false) {
        throw new Error(responseJson?.message || 'Unable to save Revenue MIS entry.');
      }

      snackbarOpen(responseJson?.message || 'Revenue MIS entry saved successfully.', 'success');
      setPendingAccrualEntry(null);
      setUploadDuplicateRows([]);
      setAccrualDialogState((prev) => ({ ...prev, isOpen: false, rowId: '' }));
      refreshRevenueMisData();
    } catch (error) {
      snackbarOpen(error?.message || 'Unable to save Revenue MIS entry.', 'error', 6000);
    } finally {
      setIsAccrualSubmitting(false);
    }
  }, [
    authToken,
    refreshRevenueMisData,
    revenueMisApiUrl,
    snackbarOpen,
    revenueMisUserInfo,
  ]);

  const handleSubmitAccrualDialog = useCallback(() => {
    const selectedRow = detailedTableData.find((row) => row.__rowId === accrualDialogState.rowId) || {};
    const entry = {
      id: readFirstPresent(selectedRow, 'revenueDetailId'),
      sourceRecordId: readFirstPresent(selectedRow, 'revenueSourceId', 'revenueDetailId'),
      revenueRowSource: readFirstPresent(selectedRow, 'revenueRowSource'),
      jvNo: accrualDialogFormDraft.jvNumber,
      projectId: accrualDialogFormDraft.projectId,
      revenueType: accrualDialogFormDraft.revenueType,
      accrualDate: accrualDialogFormDraft.accrualDate,
      accruedEntity: accrualDialogFormDraft.accruedEntity,
      milestoneId: accrualDialogFormDraft.milestoneId,
      amount: parseRevenueUploadAmount(accrualDialogFormDraft.amountInr),
      remarks: accrualDialogFormDraft.remarks,
    };

    submitRevenueAccrualEntry(entry, false);
  }, [
    accrualDialogFormDraft,
    accrualDialogState.rowId,
    detailedTableData,
    submitRevenueAccrualEntry,
  ]);

  const handleConfirmUploadReplace = useCallback(async () => {
    if (pendingAccrualEntry) {
      await submitRevenueAccrualEntry(pendingAccrualEntry, true);
      return;
    }

    if (!Array.isArray(pendingUploadRows) || pendingUploadRows.length === 0) {
      return;
    }

    await submitRevenueUpload(pendingUploadRows, true);
  }, [pendingAccrualEntry, pendingUploadRows, submitRevenueAccrualEntry, submitRevenueUpload]);

  // FIX: handleTableStateHandler no longer triggers fetchAllDetailedData.
  // That responsibility belongs solely to handleRevenueMisApiResponse to
  // avoid the double-fetch that was a major source of filter-apply lag.
  const handleTableStateHandler = useCallback((getTableState) => {
    tableStateGetterRef.current = getTableState;
    const tableState = getTableState?.();
    const configFilters = getAppliedFiltersFromConfiguration(tableState?.tableConfiguration || []);
    const stateFilters = Array.isArray(tableState?.filters) ? tableState.filters : [];
    const appliedFilters = stateFilters.filter(hasFilterValue).length > 0
      ? stateFilters.filter(hasFilterValue)
      : configFilters;

    const nextHasApplied = appliedFilters.length > 0
      || Boolean(getSelectedProjectFromConfiguration(tableState?.tableConfiguration || []));

    if (nextHasApplied) {
      stateRef.current.activeFilterParams = appliedFilters;
    }

    setHasAppliedFilters((prev) => (prev === nextHasApplied ? prev : nextHasApplied));
  }, []);

  // ── Animation loader ─────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    import('../../../Assets/images/json/dar_reseller_report_load.json').then(
      ({ default: data }) => {
        if (!cancelled) setAnimationData(data);
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Clear detailed data when filters are cleared ─────────────────────────
  useEffect(() => {
    if (!hasAppliedFilters) {
      stateRef.current.detailedFetchController?.abort();
      stateRef.current.detailedFetchController = null;
      setIsDetailedTableLoading(false);
      if (consolidatedTableData.length > 0) setConsolidatedTableData([]);
      if (detailedTableData.length > 0) setDetailedTableData([]);
      consolidatedPaginationRef.current = DEFAULT_REVENUE_MIS_PAGINATION;
      detailedPaginationRef.current = DEFAULT_REVENUE_MIS_PAGINATION;
      setConsolidatedPagination(DEFAULT_REVENUE_MIS_PAGINATION);
      setDetailedPagination(DEFAULT_REVENUE_MIS_PAGINATION);
      if (selectedDetailedRowIdsRef.current.size > 0) {
        setSyncedSelectedDetailedRowIds(new Set());
      }
      stateRef.current.lastFilterFingerprint = '';
      stateRef.current.activeFilterParams = null;
    }
  }, [
    consolidatedTableData.length,
    detailedTableData.length,
    hasAppliedFilters,
    setSyncedSelectedDetailedRowIds,
  ]);

  useEffect(() => {
    if (!hasAppliedFilters || !Array.isArray(stateRef.current.activeFilterParams)) {
      return;
    }

    const appliedFilters = stateRef.current.activeFilterParams;
    const fingerprint = getFilterFingerprint(appliedFilters);
    if (!fingerprint || fingerprint === stateRef.current.lastFilterFingerprint) {
      return;
    }

    stateRef.current.lastFilterFingerprint = fingerprint;
    fetchAllDetailedData(appliedFilters);
  }, [fetchAllDetailedData, hasAppliedFilters]);

  // ── Lottie animation ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!animationData || !animationContainer.current || hasAppliedFilters) return undefined;
    const animation = lottie.loadAnimation({
      container: animationContainer.current,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      animationData,
    });
    return () => animation.destroy();
  }, [animationData, hasAppliedFilters]);

  // ── Main table DOM enhancements ───────────────────────────────────────────
  useEffect(() => {
    const rootNode = tableContainerRef.current;
    let retryTimer = null;
    let retryCount = 0;
    const maxToolbarSyncRetries = 20;

    const handleResize = () => {
      setTableScrollHeight(`${Math.floor(window.innerHeight * 0.68)}px`);
    };

    const runEnhancements = () => {
      observer.disconnect();
      applyRevenueMisTableViewport(rootNode);
      applyBodyCellHoverTitles(rootNode);
      applyToolbarButtonClasses(rootNode);
      reorderToolbarButtons(rootNode);
      syncConsolidatedToolbarButtons(
        rootNode,
        pageHeaderActionsRef.current,
        consolidatedHeaderActionsRef.current,
      );

      if (hasAppliedFilters && consolidatedHeaderActionsRef.current) {
        const hasDownloadButton = consolidatedHeaderActionsRef.current.querySelector('.download-excel-button');
        if (!hasDownloadButton && retryCount < maxToolbarSyncRetries) {
          retryCount += 1;
          clearTimeout(retryTimer);
          retryTimer = setTimeout(runEnhancements, 150);
        } else if (hasDownloadButton) {
          retryCount = 0;
        }
      }

      if (rootNode) {
        observer.observe(rootNode, { childList: true, subtree: true, characterData: false });
      }
    };

    let debounceTimer = null;
    // FIX: Increase debounce from 80ms → 150ms to reduce observer firing rate,
    // and use subtree:false where possible to shrink the mutation surface.
    const observer = new MutationObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(runEnhancements, 150);
    });

    if (rootNode) {
      observer.observe(rootNode, { childList: true, subtree: true, characterData: false });
    }

    const initialTimer = setTimeout(runEnhancements, 300);
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(initialTimer);
      clearTimeout(retryTimer);
      clearTimeout(debounceTimer);
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [consolidatedPagination.page, consolidatedPagination.total, consolidatedTableData.length, hasAppliedFilters]);

  // ── Detailed section DOM enhancements ────────────────────────────────────
  // FIX: Keep stable refs for the callbacks passed into applyDetailedRevenueMisEnhancements
  // so the observer doesn't capture stale closures and we can avoid listing
  // every handler in the deps array (which caused the effect to re-run and
  // re-observe on every selection toggle).
  const detailedEnhancementCallbacksRef = useRef({});
  detailedEnhancementCallbacksRef.current = {
    onToggleRowSelection: handleToggleDetailedRowSelection,
    onToggleAllRowSelection: handleToggleAllDetailedRowSelection,
    onOpenDeleteDialog: handleOpenDetailedDeleteDialog,
    onOpenAccrualDialog: handleOpenAccrualDialog,
  };

  useEffect(() => {
    if (!hasAppliedFilters) return undefined;

    const detailedRootNode = detailedSectionRef.current;
    if (!detailedRootNode) return undefined;

    // Reset the width-applied guard whenever data changes so column widths
    // are re-applied after TableUtility paints new rows.
    detailedRootNode.removeAttribute('data-widths-applied');

    // Use a ref-box so runDetailedEnhancements can always read the live observer
    // instance — fixes the null-closure bug where observer?.disconnect() and
    // observer?.observe() inside the callback were no-ops because the closure
    // captured observer before it was assigned.
    const observerBox = { current: null };
    let debounceTimer = null;

    const runDetailedEnhancements = () => {
      // Pause the observer while we write to the DOM so our own innerHTML
      // changes don't immediately re-trigger another enhancement run.
      observerBox.current?.disconnect();

      applyRevenueMisTableViewport(detailedRootNode);
      applyBodyCellHoverTitles(detailedRootNode);
      applyDetailedRevenueMisEnhancements(
        detailedRootNode,
        detailedTableData,
        selectedDetailedRowIdsRef.current,
        { canDelete, canUpdate },
        detailedEnhancementCallbacksRef.current.onToggleRowSelection,
        detailedEnhancementCallbacksRef.current.onToggleAllRowSelection,
        detailedEnhancementCallbacksRef.current.onOpenDeleteDialog,
        detailedEnhancementCallbacksRef.current.onOpenAccrualDialog,
      );
      applyToolbarButtonClasses(detailedRootNode);
      syncDetailedToolbarButtons(detailedRootNode, detailedHeaderActionsRef.current);

      // Resume observing only after our writes are done.
      observerBox.current?.observe(detailedRootNode, { childList: true, subtree: true });
    };

    observerBox.current = new MutationObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(runDetailedEnhancements, 150);
    });

    // Start observing before the initial RAF so any TableUtility DOM paint
    // that happens before the frame fires is also caught.
    observerBox.current.observe(detailedRootNode, { childList: true, subtree: true });

    const rafId = requestAnimationFrame(runDetailedEnhancements);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(debounceTimer);
      observerBox.current?.disconnect();
    };
  }, [canDelete, canUpdate, detailedTableData, hasAppliedFilters]);

  // ── Table configurations ─────────────────────────────────────────────────
  // FIX: Split `hasAppliedFilters` out of the main configuration memo.
  // Previously, toggling filters caused the entire configuration object to be
  // recreated, which triggered a full TableUtility re-render on every filter
  // interaction. toolBarSettings is the only thing that changes with the flag,
  // so we patch just that field via a separate memo.
  useEffect(() => {
    if (!hasAppliedFilters) return;

    const detailedRootNode = detailedSectionRef.current;
    if (!detailedRootNode) return;

    applyDetailedRevenueMisEnhancements(
      detailedRootNode,
      detailedTableData,
      selectedDetailedRowIdsRef.current,
      { canDelete, canUpdate },
      detailedEnhancementCallbacksRef.current.onToggleRowSelection,
      detailedEnhancementCallbacksRef.current.onToggleAllRowSelection,
      detailedEnhancementCallbacksRef.current.onOpenDeleteDialog,
      detailedEnhancementCallbacksRef.current.onOpenAccrualDialog,
    );
  }, [canDelete, canUpdate, detailedTableData, hasAppliedFilters, selectedDetailedRowIds]);

  const baseConfiguration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: true,
      baseURL: revenueMisConsolidatedApiUrl,
      showToolBar: true,
      showPagination: false,
      showTieredMenu: true,
      userInfo: revenueMisUserInfo,
      headers: { token: authToken },
      apiEndpoints: {
        getAllViews: { endpoint: '', enabled: false },
        getFilteredData: { endpoint: '', enabled: true },
        saveOrUpdateView: { endpoint: '', enabled: false },
        deleteView: { endpoint: '', enabled: false },
        download: {
          endpoint: 'export',
          enabled: true,
          filename: () => 'Consolidated_Revenue_MIS',
          sheetName: () => 'Consolidated Revenue MIS',
          reportTitle: () => 'Consolidated Revenue MIS',
          transformResponse: (payload) => ({
            ...payload,
            tableConfiguration: payload.headings.map((header) => ({
              header,
              numeric: /Total Revenue\((INR|USD)\)|Budgeted FX rate/.test(header),
            })),
          }),
        },
      },
      handler: {
        setTableStateHandler: handleTableStateHandler,
        handleFilteredApiResponse: handleRevenueMisApiResponse,
      },
    },
    tableSettings: {
      size: 'small',
      pageSize: 50,
      tableConfig: initialTableConfig,
      tableScrollHeight,
      loaderSkeleton: { rows: 10, bodyCells: 2, columns: 8 },
      paginationConfig: {
        ...consolidatedPagination,
        page: 1,
        limit: Math.max(
          Number(consolidatedPagination.total) || 0,
          REVENUE_MIS_SHOW_ALL_ROWS_LIMIT,
        ),
        onPaginationChange: handleConsolidatedPaginationChange,
        rowsPerPageOptions: REVENUE_MIS_ROWS_PER_PAGE_OPTIONS,
      },
      showExpandCollapse: false,
      maxFrozenColumns: { desktop: 2, tablet: 2, mobile: 1 },
    },
  }), [
    authToken,
    consolidatedPagination,
    handleTableStateHandler,
    handleConsolidatedPaginationChange,
    handleRevenueMisApiResponse,
    initialTableConfig,
    tableScrollHeight,
    revenueMisConsolidatedApiUrl,
    revenueMisUserInfo,
  ]);

  // FIX: Only toolBarSettings depends on hasAppliedFilters — patch it here
  // rather than baking hasAppliedFilters into the full configuration memo.
  const detailedTableScrollHeight = tableScrollHeight === '400px' ? '360px' : tableScrollHeight;

  const consolidatedToolbarItems = useMemo(() => [
    canCreate ? (
      <button
        key="revenue-mis-toolbar-more"
        type="button"
        className="revenue-mis-section-action-btn"
        aria-label="Revenue MIS actions"
        title="More actions"
        onClick={handleOpenActionsMenu}
      >
        <img src={verticalDotIcon} alt="" className="revenue-mis-section-action-icon" />
      </button>
    ) : null,
  ].filter(Boolean), [
    canCreate,
    handleOpenActionsMenu,
  ]);

  const detailedToolbarItems = useMemo(() => [
    canDelete ? (
      <button
        key="delete-detailed-revenue-mis"
        type="button"
        className="revenue-mis-detailed-action-btn"
        aria-label="Delete detailed revenue MIS row"
        onClick={() => handleOpenDetailedDeleteDialog()}
        title="Delete"
      >
        <img src={deleteIcon} alt="" className="revenue-mis-detailed-action-icon" />
      </button>
    ) : null,
    canCreate ? (
      <button
        key="add-detailed-revenue-mis"
        type="button"
        className="revenue-mis-detailed-action-btn revenue-mis-add-action-btn"
        aria-label="Add detailed revenue MIS row"
        onClick={() => handleOpenAccrualDialog()}
        title="Add"
      >
        <img src={addIcon} alt="" />
      </button>
    ) : null,
  ].filter(Boolean), [
    canCreate,
    canDelete,
    handleOpenAccrualDialog,
    handleOpenDetailedDeleteDialog,
  ]);

  const configuration = useMemo(() => ({
    ...baseConfiguration,
    generalSettings: {
      ...baseConfiguration.generalSettings,
      toolBarSettings: {
        showAddNew: false,
        showDownloadExcel: true,
        showColumnCustomize: false,
        showColumnFilterMenu: true,
        showHelp: false,
        showSearch: false,
        showToolBarHeading: true,
        toolBarHeading: 'Consolidated Revenue MIS',
        downloadExcelFileName: () => 'Consolidated_Revenue_MIS',
        downloadExcelSheetName: () => 'Consolidated Revenue MIS',
        showSaveView: false,
        showReset: false,
        showExpandCollapse: false,
        showFilterOption: true,
        additionalElements: consolidatedToolbarItems,
      },
    },
  }), [baseConfiguration, consolidatedToolbarItems]);

  const detailedTableConfiguration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: false,
      showPagination: false,
      baseURL: revenueMisDetailedApiUrl,
      headers: { token: authToken },
      userInfo: revenueMisUserInfo,
      apiEndpoints: {
        download: {
          endpoint: 'export',
          enabled: true,
          filename: () => 'Detailed_Revenue_MIS',
          sheetName: () => 'Detailed Revenue MIS',
          reportTitle: () => 'Detailed Revenue MIS',
          transformResponse: (rows) => buildTableUtilityLocalExport(
            rows,
            detailedTableConfig,
            ['rowSelector', 'rowActions'],
          ),
        },
      },
      toolBarSettings: {
        showAddNew: false,
        showDownloadExcel: true,
        showColumnCustomize: false,
        showColumnFilterMenu: false,
        showHelp: false,
        showSearch: false,
        showToolBarHeading: true,
        toolBarHeading: 'Detailed Revenue MIS',
        downloadExcelFileName: () => 'Detailed_Revenue_MIS',
        downloadExcelSheetName: () => 'Detailed Revenue MIS',
        showSaveView: false,
        showReset: false,
        showExpandCollapse: false,
        showFilterOption: false,
        additionalElements: detailedToolbarItems,
      },
      showToolBar: true,
    },
    tableSettings: {
      size: 'small',
      pageSize: 50,
      tableConfig: detailedTableConfig,
      tableScrollHeight: detailedTableScrollHeight,
      loaderSkeleton: { rows: 10, bodyCells: 2, columns: 8 },
      paginationConfig: {
        ...detailedPagination,
        page: 1,
        limit: Math.max(detailedTableData.length, 1),
        total: detailedTableData.length,
        onPaginationChange: handleDetailedPaginationChange,
        rowsPerPageOptions: REVENUE_MIS_ROWS_PER_PAGE_OPTIONS,
      },
      showExpandCollapse: false,
      maxFrozenColumns: { desktop: 3, tablet: 3, mobile: 1 },
    },
  }), [
    authToken,
    detailedTableData.length,
    detailedTableConfig,
    detailedPagination,
    detailedToolbarItems,
    detailedTableScrollHeight,
    handleDetailedPaginationChange,
    revenueMisDetailedApiUrl,
    revenueMisUserInfo,
  ]);

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className={`revenue-mis-outer-layer ${hasAppliedFilters ? '' : 'revenue-mis-awaiting-project'}`.trim()}>
      <div className="revenue-mis-table-container">
        <RevenueMisPageHeader actionsHostRef={pageHeaderActionsRef} />
        <div ref={tableContainerRef} className="revenue-mis-table-utility table-utility">
          {hasAppliedFilters && (
            <RevenueMisSectionTitleBar
              title="Consolidated Revenue MIS"
              actionsHostRef={consolidatedHeaderActionsRef}
            />
          )}
          <TableUtility
            configuration={configuration}
            showFilterOption
          />
          <RevenueMisActionsMenu
            actionsMenuAnchorElement={actionsMenuAnchorElement}
            canCreate={canCreate}
            onCloseActionsMenu={handleCloseActionsMenu}
            onUploadRevenue={handleUploadRevenue}
          />
        </div>
        {!hasAppliedFilters && (
          <RevenueMisInitialState animationContainer={animationContainer} />
        )}

        {hasAppliedFilters && (
          <div className="revenue-mis-detailed-section">
            <RevenueMisSectionTitleBar
              title="Detailed Revenue MIS"
              actionsHostRef={detailedHeaderActionsRef}
            />
            <div
              ref={detailedSectionRef}
              className="revenue-mis-table-utility table-utility revenue-mis-detailed-table-utility"
            >
              {isDetailedTableLoading ? (
                <RevenueMisDetailedTableSkeleton rows={10} columns={8} />
              ) : (
                <TableUtility
                  key="detailed-revenue-mis-table"
                  configuration={detailedTableConfiguration}
                  data={detailedTableData}
                />
              )}
            </div>
          </div>
        )}

        <RevenueMisDeleteDialog
          isOpen={deleteDialogRowIds.length > 0}
          onClose={handleCloseDetailedDeleteDialog}
          onConfirm={handleConfirmDetailedDelete}
        />
        <RevenueMisUploadReplaceDialog
          isOpen={uploadDuplicateRows.length > 0}
          duplicateRows={uploadDuplicateRows}
          isSubmitting={isUploadSubmitting || isAccrualSubmitting}
          description={
            pendingAccrualEntry
              ? 'This Revenue MIS entry already exists. Do you want to replace it?'
              : 'Some uploaded entries already exist. Do you want to replace them?'
          }
          showRowNumbers={!pendingAccrualEntry}
          onClose={handleCloseUploadReplaceDialog}
          onConfirm={handleConfirmUploadReplace}
        />
        <RevenueMisAccrualDialog
          isOpen={accrualDialogState.isOpen}
          mode={accrualDialogState.mode}
          formData={accrualDialogFormDraft}
          options={accrualDialogOptions}
          isOptionsLoading={isAccrualOptionsLoading}
          isSubmitting={isAccrualSubmitting}
          onChange={handleChangeAccrualDialogField}
          onClose={handleCloseAccrualDialog}
          onSubmit={handleSubmitAccrualDialog}
        />
        <RevenueMisUploadDialog
          isOpen={isUploadDialogOpen}
          selectedFileName={uploadFileName}
          isSubmitting={isUploadSubmitting}
          onFileChange={handleUploadFileChange}
          onDownloadSample={handleDownloadSampleFile}
          onClose={handleCloseUploadDialog}
          onSubmit={handleSubmitUploadDialog}
        />
      </div>
    </div>
  );
};

export default RevenueMIS;

